/**
 * HannoNexus — Multi-language content (PT · EN · DE · FR).
 * Central translation source for HannoNexus rebrand.
 * Consumed via useHannoContent() hook.
 */

export const HANNO_CONTENT = {
  pt: {
    brand: { powered_by: "Powered by BSGOTECH", tagline: "Integrated Infrastructure Ecosystem" },
    nav: {
      home: "Home", hx_energy: "HX Energy", hx_automotive: "HX Automotive", hx_systems: "HX Systems",
      hx_infrastructure: "HX Infrastructure", hx_academy: "HX Academy", projects: "Projects", contact: "Contact",
      cta_partnerships: "Strategic Partnerships",
    },
    search: {
      trigger: "Pesquisar", placeholder: "Pesquisar páginas, projetos, ecossistemas…",
      empty: "Sem resultados", hint_navigate: "Navegar", hint_select: "Abrir", hint_close: "Fechar",
      groups: { pages: "Páginas", ecosystems: "Ecossistemas HX", projects: "Projetos", presentations: "Apresentações" },
    },
    mou: {
      badge: "MEMORANDUM OF UNDERSTANDING",
      title: "Memorandum of Understanding",
      subtitle: "Construindo uma Parceria Estratégica para Energia Sustentável em Cabo Verde e na CPLP.",
      cta_download: "Descarregar MOU", cta_request: "Solicitar Parceria", cta_login: "Área de Parceiros",
      logos_row: "Ecossistema de Parceiros Estratégicos",
      overview: {
        eyebrow: "Visão Geral da Parceria", title: "Uma aliança industrial", accent: "de longo prazo.",
        cards: [
          { icon: "target",   title: "Missão",          body: "Democratizar o acesso a energia solar de nível utility, armazenamento e mobilidade elétrica nos países lusófonos através de tecnologia OEM tier-1 e industrialização local." },
          { icon: "eye",      title: "Visão",           body: "Tornar Cabo Verde e São Tomé e Príncipe hubs de referência CPLP em energia renovável, com autonomia técnica, financeira e formativa até 2030." },
          { icon: "flag",     title: "Objetivos",       body: "Fornecer, instalar e operar sistemas fotovoltaicos, BESS e redes inteligentes — com transferência tecnológica, criação de emprego qualificado e formação certificada." },
          { icon: "compass",  title: "Estratégia de Longo Prazo", body: "Modelo replicável nos 9 países da CPLP: OEM + private-label + montagem local + academy — orquestrado pela plataforma HannoNexus." },
        ],
      },
      structure: {
        eyebrow: "Estrutura da Parceria", title: "Como se organiza a", accent: "rede HannoNexus.",
        nodes: {
          root:      { title: "HX Ecosystems | BSGOTECH",           sub: "Operador · Arquitetura & Governança" },
          hn:        { title: "HannoNexus Solar",                    sub: "Plataforma comercial CPLP" },
          network:   { title: "Rede de Parceiros Estratégicos",      sub: "Fabricantes · Serviços · Instituições" },
          bluesun:   { title: "Bluesun Solar",                       sub: "Parceiro Estratégico de Fabrico Energético", tag: "Manufacturing" },
          services:  { title: "Parceiros Comerciais de Serviços",    sub: "Serviços digitais e de gestão" },
          dwh:       { title: "Digital Wise Hustle",                 sub: "Parceiro de Serviços Digitais", tag: "Digital",
                       services: ["Marketing Digital", "Branding", "Desenvolvimento Web", "Crescimento de Negócio", "Transformação Digital"],
                       model: "A HannoNexus comercializa estes serviços e retém margem comercial." },
          aaban:     { title: "AABAN Solutions",                     sub: "Parceiro de Soluções Empresariais", tag: "Business",
                       services: ["Consultoria Empresarial", "Soluções Corporativas", "Gestão de Projetos", "Assessoria Estratégica"],
                       model: "A HannoNexus comercializa estes serviços e retém margem comercial." },
        },
      },
      bluesun: {
        eyebrow: "Bluesun Solar", title: "Parceiro Estratégico de", accent: "Fabrico Energético.",
        lede: "Bluesun Solar é o parceiro OEM de fabrico da HannoNexus — módulos fotovoltaicos, inversores e sistemas de bateria com engenharia certificada, documentação técnica e certificações internacionais.",
        pills: ["Fabrico Solar", "OEM", "Private Label", "Módulos PV", "Inversores", "Sistemas de Bateria", "Suporte Técnico", "Engenharia", "Documentação", "Certificações", "Expansão de Mercado Conjunta"],
        flow: { manufacturer: "Fabricante", hannonexus: "HannoNexus Solar", customer: "Cliente Final",
                m_desc: "Bluesun · OEM tier-1", h_desc: "Comercialização · Engenharia · Serviço", c_desc: "Governos · Empresas · Utility" },
      },
      areas: {
        eyebrow: "Áreas de Cooperação", title: "Onde a parceria", accent: "gera valor.",
        list: [
          { icon: "package",     title: "Fornecimento de Equipamento", desc: "Painéis, inversores, BESS e cabos com origem OEM certificada." },
          { icon: "factory",     title: "Fabrico OEM",                  desc: "Especificação e fabrico de produtos co-desenvolvidos." },
          { icon: "tag",         title: "Private Label",                desc: "Portfolio próprio HannoNexus com identidade e garantia." },
          { icon: "cpu",         title: "Engenharia",                   desc: "Desenho de sistema, dimensionamento, simulação e as-built." },
          { icon: "sun",         title: "Projetos Solares",             desc: "Projetos utility-scale, comerciais e residenciais chave-na-mão." },
          { icon: "graduation",  title: "Formação Técnica",             desc: "HX Academy · currículos certificados de instalação e O&M." },
          { icon: "wrench",      title: "Assistência Pós-Venda",        desc: "Rede de serviço autorizado e peças originais." },
          { icon: "network",     title: "Rede de Instalação",           desc: "Parceiros locais certificados no HX Install Network." },
          { icon: "globe",       title: "Serviços Digitais",            desc: "Digital Wise Hustle · marketing, branding, web, growth." },
          { icon: "briefcase",   title: "Soluções Empresariais",        desc: "AABAN Solutions · consultoria, PMO, advisory estratégico." },
          { icon: "map",         title: "Expansão Regional",            desc: "Replicação do modelo em Angola, Moçambique, GB, Timor." },
        ],
      },
      benefits: {
        eyebrow: "Porque ser Parceiro", title: "Quatro pilares de", accent: "valor mútuo.",
        cards: [
          { icon: "trending-up", title: "Para a HannoNexus", body: "Acesso a fabrico OEM tier-1, portfolio private-label e velocidade go-to-market nos mercados CPLP." },
          { icon: "layers",      title: "Para a Bluesun Solar", body: "Canal comercial estruturado nos países lusófonos, montagem local e representação técnica." },
          { icon: "users",       title: "Para os Clientes", body: "Preço competitivo, garantia local, assistência técnica próxima e formação certificada." },
          { icon: "flag",        title: "Para Cabo Verde", body: "Emprego qualificado, redução da dependência energética e transferência tecnológica real." },
        ],
      },
      roadmap: {
        eyebrow: "Roadmap", title: "Seis fases.", accent: "Um plano.",
        phases: [
          { phase: "Fase 1", title: "Assinatura do MOU",                       desc: "Formalização do compromisso estratégico entre as partes." },
          { phase: "Fase 2", title: "Registo da Empresa",                       desc: "HX Ecosystems | BSGOTECH Lda. · sede em Cabo Verde." },
          { phase: "Fase 3", title: "Primeiro Contentor Solar",                 desc: "Chegada e desalfandegamento do primeiro lote de equipamento OEM." },
          { phase: "Fase 4", title: "Solar Experience Center",                  desc: "Showroom técnico e demo permanente na Praia." },
          { phase: "Fase 5", title: "Installer Academy",                        desc: "Primeira cohort HX Academy · certificação nacional." },
          { phase: "Fase 6", title: "Expansão Regional",                        desc: "Replicação em STP, Angola, Moçambique e Guiné-Bissau." },
        ],
      },
      team: {
        eyebrow: "Equipa", title: "Uma equipa técnica", accent: "sediada localmente.",
        roles: [
          { role: "CEO & Fundador",          icon: "star" },
          { role: "Equipa de Instalação",    icon: "hard-hat" },
          { role: "Engenharia",              icon: "cpu" },
          { role: "Operações de Terreno",    icon: "map" },
          { role: "Finanças",                icon: "line-chart" },
          { role: "Marketing & Estratégia",  icon: "megaphone" },
          { role: "Parceiros Técnicos",      icon: "handshake" },
        ],
      },
      gallery: { eyebrow: "Galeria de Projetos", title: "A obra feita.", accent: "Imagem por imagem." },
      docs: {
        eyebrow: "Centro Documental", title: "Documentos", accent: "públicos e privados.",
        public_title: "Documentos Públicos",
        public: [
          { icon: "file-text", label: "MOU PDF" },
          { icon: "book",      label: "Catálogo de Produtos" },
          { icon: "slides",    label: "Apresentação Institucional" },
          { icon: "award",     label: "Certificados" },
        ],
        private_title: "Portal Privado de Parceiros",
        private: [
          { icon: "file-signature", label: "Contratos" },
          { icon: "receipt",        label: "Fatura Pró-Forma" },
          { icon: "file-code",      label: "Documentos Técnicos" },
          { icon: "briefcase",      label: "Acordos Comerciais" },
          { icon: "line-chart",     label: "Relatórios" },
        ],
        login_hint: "Acesso protegido por password", login_cta: "Entrar no Portal",
      },
      form: {
        eyebrow: "Solicitar Parceria", title: "Vamos", accent: "construir juntos.",
        lede: "Preencha o formulário e a equipa HannoNexus responde em até 48h úteis.",
        fields: {
          company: "Empresa", country: "País", website: "Website",
          contact: "Pessoa de Contacto", email: "Email", phone: "Telefone",
          type: "Tipo de Parceria",
          type_options: ["Distribuidor", "Instalador", "Investidor", "Parceiro Tecnológico", "Governo", "Parceiro de Negócio"],
          message: "Mensagem",
        },
        submit: "SOLICITAR PARCERIA", success: "Pedido recebido — resposta em 48h.", error: "Não foi possível enviar. Tente novamente.",
      },
    },
    hero: {
      subtitle_p1: "Integrated", subtitle_accent: "Infrastructure Ecosystem",
      keywords: ["Energy", "Mobility", "Infrastructure", "Digital Systems"],
      lede: "A HannoNexus concebe e opera ecossistemas integrados de energia renovável, mobilidade elétrica, infraestrutura inteligente e plataformas digitais, acelerando o desenvolvimento sustentável dos países lusófonos.",
      cta_explore: "Explore the Ecosystem", cta_partnerships: "Strategic Partnerships", scroll_hint: "Scroll to discover",
    },
    vision: {
      eyebrow: "The Vision", title: "Uma Plataforma.", accent: "Um Ecossistema.",
      p: "HX Ecosystems é o núcleo operacional da HannoNexus — cinco camadas integradas que cobrem toda a cadeia de valor, desde a produção de energia até à mobilidade, passando pela infraestrutura física, plataformas digitais e formação técnica.",
    },
    ecosystems: {
      eyebrow: "HX Ecosystems", title: "O núcleo operacional da", accent: "HannoNexus",
      lede: "Cinco ecossistemas integrados. Uma única plataforma. Um só operador.",
      includes: "Inclui", learn_more: "Saiba Mais",
      strategic_badge: "Projeto Estratégico",
    },
    eco: {
      "hx-energy": {
        name: "HX HannoNexus Energy", short: "HX Energy", tagline: "Ecossistema de energia inteligente",
        description: "Energia inteligente para produção, armazenamento e distribuição. Do sol à rede, do painel ao ponto de carregamento — uma cadeia de valor totalmente integrada.",
        subs: {
          "HX Solar": "Produção fotovoltaica residencial, comercial e utility-scale.",
          "HX Core": "Sistemas de armazenamento (BESS) residencial, comercial e industrial.",
          "HX Pulse": "Plataforma digital de monitorização, IA e telemetria em tempo real.",
          "HX Charge": "Rede de carregamento AC/DC para EV — cafés, hotéis, corredores.",
          "HX Install Network": "Rede certificada de parceiros de instalação e manutenção.",
          "HX Academy": "Formação e certificação técnica em energia solar e armazenamento.",
        },
      },
      "hx-automotive": {
        name: "HX HannoNexus Automotive", short: "HX Automotive", tagline: "Ecossistema de mobilidade elétrica",
        description: "Mobilidade elétrica completa — do citadino compacto ao grand tourer, das bicicletas OBOWAY às motas elétricas. Portfolio private-label proprietário, desenvolvido para os mercados lusófonos, com montagem local e assistência técnica autorizada.",
        subs: {
          "HX EV": "Portfólio private-label de veículos elétricos: Nudi, Zuri, Pirys, Mionga, Mionga Cargo, Atlan.",
          "OBOWAY": "Marca própria de mobilidade leve elétrica: e-bikes e motas 100% elétricas.",
          "HX Assembly": "Centros de montagem local com transferência tecnológica OEM.",
          "HX Battery": "Baterias de tração, second-life e sistemas de armazenamento veicular.",
          "HX Mobility": "Plataforma de mobilidade partilhada e serviços urbanos.",
          "HX Fleet": "Gestão inteligente de frotas com telemetria e otimização de rotas.",
          "HX Service": "Rede de assistência técnica autorizada e peças originais.",
        },
        vehicles: {
          "HX Nudi": { segment: "Compact Urban EV", desc: "Citadino compacto elétrico — mobilidade urbana diária, ideal para cidades e ilhas." },
          "HX Zuri": { segment: "Executive Sedan", desc: "Sedan executivo elétrico com autonomia estendida — corporate & premium mobility." },
          "HX Pirys": { segment: "Adventure SUV", desc: "SUV elétrico off-road-ready para terrenos vulcânicos e estradas costeiras." },
          "HX Mionga": { segment: "Utility Pickup", desc: "Pickup elétrica utilitária — agricultura, obras públicas e frotas de serviço." },
          "HX Mionga Cargo": { segment: "Commercial Van", desc: "Van elétrica de carga para logística de última milha e distribuição comercial." },
          "HX Atlan": { segment: "Flagship Grand Tourer", desc: "Grand tourer elétrico topo de gama — o carro de assinatura HannoNexus." },
          "OBOWAY E-Bike": { segment: "Electric Bicycle · OBOWAY", desc: "Bicicleta elétrica OBOWAY — mobilidade leve urbana e turística, ideal para ilhas e cidades históricas." },
          "OBOWAY Moto": { segment: "Electric Motorcycle · OBOWAY", desc: "Mota elétrica OBOWAY — solução ágil de mobilidade urbana e last-mile delivery com zero emissões." },
        },
      },
      "hx-systems": {
        name: "HX Systems", short: "HX Systems", tagline: "Plataformas digitais & infraestrutura tecnológica",
        description: "A camada digital que suporta toda a operação HannoNexus — cloud soberana, inteligência artificial, dados e IoT industrial.",
        subs: {
          "HX Cloud": "Cloud pública, híbrida e edge — infraestrutura escalável e soberana.",
          "HX AI": "Modelos de IA, LLMs privados, visão computacional e otimização preditiva.",
          "HX HUB": "Centro digital unificado — dashboards, workflows e integrações empresariais.",
          "HX Data": "Data lakes, warehouses e pipelines de analytics em tempo real.",
          "HX IoT": "Plataforma IoT industrial — sensores, telemetria e gestão de dispositivos.",
        },
      },
      "hx-infrastructure": {
        name: "HX Infrastructure", short: "HX Infrastructure", tagline: "Infraestruturas inteligentes",
        description: "Construímos a infraestrutura física das cidades do futuro — redes elétricas, iluminação pública, água e armazenamento distribuído.",
        subs: {
          "Smart Cities": "Sistemas integrados de gestão urbana — mobilidade, energia, segurança.",
          "Microgrids": "Redes elétricas autónomas para comunidades e zonas industriais.",
          "Water Systems": "Gestão inteligente de água — dessalinização, tratamento, distribuição.",
          "Public Lighting": "Iluminação pública LED com IoT — telemetria, dimming, sensores.",
          "Energy Storage": "Armazenamento distribuído para estabilidade e resiliência da rede.",
        },
      },
      "hx-academy": {
        name: "HX Academy", short: "HX Academy", tagline: "Formação e certificação",
        description: "Formamos e certificamos os profissionais que operam o ecossistema HannoNexus — engenharia, energia, mobilidade, software e IA.",
        subs: {
          "Energy": "Instalação e operação de sistemas solares, BESS e redes inteligentes.",
          "Mobility": "Manutenção EV, gestão de frota e infraestrutura de carregamento.",
          "Software": "Engenharia de software, cloud e desenvolvimento de plataformas.",
          "AI": "Machine learning aplicado, visão computacional e IA operacional.",
          "Engineering": "Engenharia elétrica, mecânica e sistemas industriais.",
        },
      },
      "mionga": {
        name: "MIONGA Beverages", short: "MIONGA", tagline: "Ecossistema premium de lifestyle",
        description: "Bebidas de assinatura, café artesanal Moin | Olá, formatos flagship e alianças estratégicas entre a Europa (Uetze) e a CPLP — a marca de estilo de vida do grupo HannoNexus.",
        subs: {
          "MIONGA Water":     "Água mineral natural · pH 7,2 · Rica em potássio.",
          "MIONGA Sparkling": "Água mineral com gás · Refrescante e equilibrada.",
          "MIONGA Juice":     "Sumos 100% naturais · Sem conservantes.",
          "MIONGA Iced Tea":  "Chá gelado premium · Ingredientes selecionados.",
          "MIONGA Coffee":    "Blend premium · 100% Arábica.",
          "Moin | Olá Cafés": "Formato flagship urbano com Kinderecke, bar e retail.",
        },
      },

    },
    how: {
      eyebrow: "Como Funciona", title: "Da energia à", accent: "decisão em tempo real.", phase: "Fase",
      steps: [
        { step: "Produção de Energia",       desc: "Solar · Eólico · Cogeração" },
        { step: "Armazenamento Inteligente", desc: "BESS residencial · comercial · industrial" },
        { step: "Gestão Digital",            desc: "HX Cloud · HX Data · HX HUB" },
        { step: "Infraestrutura",            desc: "Smart Grid · Microgrids · Public Lighting" },
        { step: "Mobilidade Elétrica",       desc: "EV · Fleet · Charging" },
        { step: "Monitorização IA",          desc: "HX Pulse · HX AI · Manutenção Preditiva" },
        { step: "Análise em Tempo Real",     desc: "Dashboards executivos · KPIs · Alertas" },
      ],
    },
    projects: {
      eyebrow: "Projetos Estratégicos", title: "A implementação começou nos", accent: "países lusófonos.",
      components: "Componentes do Ecossistema", location: "Localização", start: "Início", jobs: "Empregos",
      next_markets: "Próximos Mercados",
      next_markets_desc: "A replicação do modelo integrado HannoNexus está prevista para os restantes países da CPLP a partir de 2030.",
      cta_title_p1: "Governo, investidor institucional ou", cta_title_accent: "parceiro estratégico?",
      cta_lede: "Trabalhamos em modelo BOT, joint-venture, private-label OEM e concessão pública.",
      cta_button: "Iniciar Parceria",
      list: {
        "cabo-verde": {
          country: "Cabo Verde", focus: "HX Energy", tagline: "Independência energética do arquipélago.",
          description: "Implementação de um ecossistema energético completo — produção solar, armazenamento, distribuição inteligente e formação técnica local.",
          highlights: ["Energia Solar", "BESS (Battery Energy Storage)", "Smart Grid inter-ilhas", "Centros Logísticos", "HX Academy"],
        },
        "sao-tome": {
          country: "São Tomé e Príncipe", focus: "HX Automotive", tagline: "Mobilidade elétrica insular.",
          description: "Ecossistema de mobilidade elétrica com montagem local, rede de carregamento, gestão digital de frota e formação técnica.",
          highlights: ["Veículos Elétricos", "Centro de Montagem", "Rede de Carregamento", "Plataforma Digital", "Formação Técnica"],
        },
        "mionga": {
          country: "Uetze → Cabo Verde", focus: "MIONGA Beverages", tagline: "Pureza que viaja. Qualidade que fica.",
          description: "Ecossistema premium de lifestyle e hospitalidade — bebidas oficiais, café de assinatura, formatos flagship e alianças estratégicas entre a Europa e a CPLP.",
          highlights: ["MIONGA Water · Sparkling · Juice · Iced Tea · Coffee", "Moin | Olá Coffee Connection · Uetze HQ", "Rota Atlântica ~3.900 km", "Frota Cargo Elétrica · Zero Emissões", "Rede Hoteleira & F&B Premium"],
          linkLabel: "Explorar MIONGA",
        },

      },
    },
    pulse: {
      eyebrow: "Plataforma Inteligente", title: "HX Pulse",
      lede: "O centro digital que monitoriza toda a operação HannoNexus em tempo real — desde a célula de bateria ao carregador, do painel solar ao dashboard executivo.",
      features: ["Inteligência Artificial", "Dashboards Executivos", "Telemetria Industrial", "Manutenção Preditiva", "Monitorização 24/7", "Starlink Connectivity"],
      chart_label: "Energy Flow · 24h", chart_status: "Real-time",
      metrics: [
        { k: "Solar Yield", v: "12.4 MW", sub: "+8.2%" },
        { k: "ESS SoC", v: "84%", sub: "Nominal" },
        { k: "EV Sessions", v: "1,247", sub: "Today" },
        { k: "CO₂ Avoided", v: "42.1 t", sub: "This month" },
      ],
    },
    impact: {
      eyebrow: "Impacto", title: "Números que", accent: "medem o futuro.",
      stats: [
        { value: "20 000+", label: "Sistemas instalados" },
        { value: "60 MW",   label: "Energia limpa" },
        { value: "500+",    label: "Profissionais certificados" },
        { value: "300+",    label: "Empregos qualificados" },
        { value: "15 000 t", label: "CO₂ evitado" },
      ],
    },
    roadmap: {
      eyebrow: "Roadmap", title: "Cinco anos.", accent: "Cinco marcos.",
      items: [
        { year: "2026", milestone: "Planeamento Estratégico",         desc: "Modelação financeira, parcerias, licenças, engenharia preliminar." },
        { year: "2027", milestone: "Centro Logístico & HX Academy",   desc: "Abertura do primeiro centro logístico e campus de formação." },
        { year: "2028", milestone: "Operação HX Energy",              desc: "Comissionamento de solar, BESS e smart grid em Cabo Verde." },
        { year: "2029", milestone: "Operação HX Automotive",          desc: "Início da montagem local de veículos elétricos e da rede de carregamento em São Tomé e Príncipe." },
        { year: "2030", milestone: "Expansão Regional",               desc: "Angola, Moçambique e Guiné-Bissau — replicação do modelo integrado." },
      ],
    },
    partners: {
      eyebrow: "Parceiros Estratégicos", title: "Construído com", accent: "instituições que constroem países.",
      categories: ["Governos", "Fabricantes OEM", "Instituições Financeiras", "Fundos ESG", "Parceiros Tecnológicos"],
    },
    investment: {
      eyebrow: "Modelo de Investimento", title: "OEM +", accent: "Private Label.",
      lede: "A HannoNexus integra tecnologia OEM Tier-1 internacional com montagem local, operação local e assistência técnica local. Trata-se de um modelo comprovado que transfere valor efetivo ao país de destino, sem comprometer a qualidade industrial.",
      advantages: ["Industrialização local", "Transferência tecnológica", "Redução de custos operacionais", "Criação de emprego qualificado", "Escalabilidade regional"],
    },
    about: {
      eyebrow: "Sobre a HannoNexus", title: "Uma plataforma dedicada aos", accent: "países lusófonos.",
      p: "A HannoNexus é uma plataforma dedicada ao desenvolvimento de ecossistemas de energia, mobilidade e infraestrutura inteligente, disponibilizando soluções tecnológicas adaptadas às necessidades dos países lusófonos — Cabo Verde, São Tomé e Príncipe, Angola, Moçambique, Guiné-Bissau e Timor-Leste.",
    },
    contact_banner: {
      title: "Let's Build the", accent: "Future Together.",
      lede: "Governos, investidores institucionais, fabricantes OEM e parceiros tecnológicos — respondemos em 48 horas.",
      cta: "Strategic Partnerships",
    },
    people: {
      eyebrow: "As Nossas Equipas", title: "Feito por pessoas.", accent: "Operado nos países lusófonos.",
      lede: "A HannoNexus é operada por engenheiros, técnicos e formadores locais — de Cabo Verde, São Tomé e Príncipe e da diáspora lusófona. Formação certificada, emprego qualificado, orgulho na obra feita.",
      roles: {
        role_solar: "Técnica Solar · HX Install Network",
        role_control: "Engenheira de Operações · HX Pulse",
        role_mechanic: "Técnico EV · HX Service",
        role_academy: "Formandos · HX Academy",
        role_network: "Equipa HX Install Network",
        role_engineer: "Engenheira Sénior · HX Energy",
      },
    },
    eco_page: {
      portfolio_eyebrow: "Portfolio", portfolio_title: "O que compõe",
      lineup_eyebrow: "Private Label · Vehicle Lineup", lineup_title: "A frota", lineup_accent: "HannoNexus.",
      lineup_lede: "Portfolio private-label completo: seis carros elétricos HX + duas OBOWAY de mobilidade leve — do citadino compacto ao grand tourer flagship, da e-bike urbana à mota elétrica.",
      private_label_electric: "Private-label · 100% electric", model: "Model",
      values: [
        { k: "Integrado", v: "Uma única plataforma operacional cobre toda a cadeia de valor." },
        { k: "Escalável", v: "Modelo replicável — de um piloto local a uma expansão continental." },
        { k: "Certificado", v: "Hardware TÜV · CE · IEC de parceiros OEM tier-1." },
      ],
      others_eyebrow: "Outros Ecossistemas", others_title: "Explore o resto da plataforma HannoNexus.",
      cta_title: "Interessado em implementar", cta_lede: "Preparamos um plano de implementação personalizado em 48 horas.",
      cta_button: "Falar com a equipa",
    },
    footer: {
      tagline_l1: "Energy · Mobility · Infrastructure ·", tagline_l2: "Digital Systems.",
      contact: "Contact", operator: "Operator", operated_by: "Integrated & operated by",
      operator_name: "BSGOTECH Infrastructure Systems",
      ecosystems: "HX Ecosystems", markets: "Mercados Estratégicos",
      copyright: "HannoNexus · Integrated Infrastructure Ecosystem",
      architected: "Architected in Germany", cplp_badge: "CPLP · Powered by BSGOTECH",
    },
    dashboards: {
      common: {
        online: "Sistema Online e Automatizado", live_dashboard: "Live Dashboard · Ao Vivo",
        open_dashboard: "Abrir Dashboard Ao Vivo", request_demo: "Solicitar demonstração",
        request_meeting: "Solicitar Reunião Executiva", other_projects: "Ver outros projetos",
        recent_alerts: "Alertas Recentes", live_feed: "HN AI · Feed ao Vivo",
        ai_recommended: "Recomendada pela HN AI", client_ecosystem: "Ecossistema de Clientes",
        who_consumes: "Quem consome o quê", replicability: "Replicabilidade",
        preview_telemetry: "Preview · Live Telemetry", grid_telemetry: "Preview · Grid Telemetry",
      },
      biopinto: {
        country_label: "São Tomé e Príncipe · Projeto-Piloto",
        title_a: "BioPintoSTP", title_b: "Quinta Modelo Conectada",
        lede: "Monitorização em tempo real via Plataforma HannoNexus (HN AI) com sensores IoT integrados e imagem satélite CropWatch — um gémeo digital operacional para agricultura de precisão em São Tomé e Príncipe.",
        featured_lede: "O primeiro gémeo digital agrícola integrado da HannoNexus: monitorização IoT em tempo real, análise de imagem satélite CropWatch, decisão automatizada pela HN AI e um modelo replicável para todos os países CPLP.",
        featured_tag: "IoT · Satélite · HN AI",
        kpi_solar: "Geração Solar", kpi_solar_sub: "Huawei FusionSolar · realtime",
        kpi_water: "Poupança Hídrica", kpi_water_sub: "vs. rega manual (mês corrente)",
        kpi_nodes: "Sensores Ativos", kpi_nodes_sub: "IoT nodes online / 130 total",
        kpi_sat: "Passagens Satélite", kpi_sat_sub: "CropWatch · NDVI + Térmico",
        loop_eyebrow: "Camada Física · Loop Fechado",
        loop_title: "Energia solar → Bombagem → Irrigação → Retorno",
        loop_badge: "Loop Físico · Solar → Bombagem → Irrigação",
        loop_overlay_title: "Fluxo integrado energia ↔ água",
        loop_overlay_sub: "Huawei FusionSolar · DIFFUL · Haishun · Zero diesel.",
        tel_solar_brand: "Huawei FusionSolar", tel_solar_title: "Geração Fotovoltaica",
        tel_solar_row1: "Geração atual", tel_solar_row2: "Consumo diesel", tel_solar_row2_val: "0 L · Zero Diesel", tel_solar_row3: "Autoconsumo hoje",
        tel_water_brand: "Bombagem DIFFUL & Irrigação Haishun", tel_water_title: "Ciclo Hídrico",
        tel_water_row1: "Fluxo de água", tel_water_row1_val: "Otimizado (AI-loop)", tel_water_row2: "Poupança acumulada", tel_water_row3: "Volume no reservatório",
        matrix_eyebrow: "Camada de Processamento · Matriz IoT",
        matrix_title: "Quatro setores produtivos monitorizados pela HN AI",
        sec_avi: "Avicultura", sec_avi_status: "Fluxo de caixa constante", sec_avi_kpi: "Segurança Proteica",
        sec_viv: "Viveiro de Plantas", sec_viv_status: "Regeneração local ativa", sec_viv_kpi: "Capacidade Utilizada",
        sec_flo: "Flores Ornamentais", sec_flo_status: "Foco: exportação de margem elevada", sec_flo_kpi: "Lotes Prontos",
        sec_hor: "Hortícolas & Fruta", sec_hor_status: "Irrigação inteligente ativa", sec_hor_kpi: "Humidade do Solo",
        forecast_eyebrow: "Camada de Interface · Decisão em Tempo Real",
        forecast_title: "Alertas da HN AI e previsão de colheita",
        forecast_head: "Previsão de Colheita",
        col_window: "Janela", col_crop: "Cultura", col_volume: "Volume", col_channel: "Canal", col_confidence: "Confiança",
        impact_eyebrow: "Impacto Social · Modelo Multicliente",
        impact_title: "Dias de Campo & ecossistema de beneficiários",
        academy_eyebrow: "Formação de Campo", academy_title: "Dias de Campo BioPintoSTP",
        academy_lede: "Sessões práticas trimestrais na quinta modelo, onde agricultores locais, alunos de escolas agrícolas e representantes municipais recebem formação em irrigação inteligente, gestão fotovoltaica e leitura de dados IoT. A HannoNexus certifica cada participante e integra os melhores no HX Academy Talent Pool.",
        academy_photo_tag: "Sessão Trimestral · Setor C",
        academy_photo_caption: "Agricultores e estudantes locais em formação sobre irrigação inteligente e leitura de dados IoT.",
        mini_participants: "Participantes 2026", mini_sessions: "Sessões realizadas", mini_schools: "Escolas envolvidas",
        cta_title: "Um modelo desenhado para escalar por todos os países CPLP.",
        cta_lede: "A arquitetura BioPintoSTP é modular: energia, água, produção e formação replicáveis em Cabo Verde, Angola, Moçambique e Guiné-Bissau, integradas na mesma HannoNexus AI.",
        alerts: [
          { time: "há 4 min", title: "Alerta Climático — Seca detetada por satélite CropWatch", action: "Irrigação Haishun ajustada automaticamente: -18% consumo hídrico nos setores B3/B4." },
          { time: "há 12 min", title: "Pico solar acima da média sazonal (+22%)", action: "Excedente redirecionado para bombagem DIFFUL — armazenamento de água na cisterna superior." },
          { time: "há 38 min", title: "Humidade do solo — Setor Hortícolas C1 em estado ideal", action: "Ciclo de rega suspenso; economia estimada de 340 L nas próximas 6 h." },
        ],
        harvest: [
          { window: "Semana 24", crop: "Alface Batávia (Setor C2)", volume: "180 kg", channel: "Mercado Local · STP-Sul" },
          { window: "Semana 26", crop: "Flores Ornamentais — Antúrios", volume: "12 lotes", channel: "Exportação · Lisboa (LIS)" },
          { window: "Semana 27", crop: "Tomate Cereja (Setor C4)", volume: "240 kg", channel: "Cadeia Hoteleira · São Tomé" },
          { window: "Semana 28", crop: "Ovos Caipira (Aviário A1)", volume: "3.400 un.", channel: "Distribuição Contínua" },
        ],
        clients: [
          { group: "Agricultores Locais", consume: "Formação Técnica + Produção" },
          { group: "Escolas & Universidades", consume: "Dias de Campo + Currículo IoT" },
          { group: "Municípios & Governo", consume: "Modelo Replicável + Dados" },
          { group: "ONGs & Cooperação", consume: "Tecnologia + Regeneração" },
        ],
      },
      cv: {
        country_label: "Cabo Verde · 10 Ilhas · Fase 1: 5 Ativas",
        title_a: "HX Cabo Verde", title_b: "Hub Energético Insular",
        status_badge: "Grid Insular · Estabilizada",
        featured_status: "Live Grid · Insular Estabilizada",
        featured_tag: "Solar · BESS · Smart Grid",
        lede: "Independência energética arquipelágica desenhada e operada pela HannoNexus (HN AI) — produção solar utility-scale, armazenamento BESS LFP, distribuição inteligente inter-ilhas e formação técnica local.",
        featured_lede: "Independência energética arquipelágica: produção solar utility-scale, armazenamento BESS LFP, distribuição inteligente inter-ilhas orquestrada pela HN AI e HX Academy para formar talento local.",
        kpi_solar: "Solar Instalado", kpi_solar_sub: "Santiago · Sal · Boa Vista",
        kpi_bess: "BESS Storage", kpi_bess_sub: "LFP · 261 kWh / módulo",
        kpi_wind: "Eólica Ativa", kpi_wind_sub: "Sal · Vestas V90",
        kpi_diesel: "Diesel Substituído", kpi_diesel_sub: "vs. baseline 2024",
        loop_eyebrow: "Camada Física · Loop Energético",
        loop_title: "Solar utility-scale → BESS → Smart Grid inter-ilhas",
        loop_badge: "Loop · Solar → BESS → Smart Grid",
        loop_overlay_title: "Fluxo integrado energia insular",
        loop_overlay_sub: "Huawei FusionSolar · CATL LFP · Vestas · HN AI orchestration.",
        tel_solar_brand: "Huawei FusionSolar · Utility-Scale", tel_solar_title: "Produção Fotovoltaica",
        tel_solar_r1: "Geração instantânea", tel_solar_r2: "Autoconsumo hoje", tel_solar_r3: "Excedente exportado",
        tel_bess_brand: "BESS LFP · CATL 261 kWh", tel_bess_title: "Armazenamento Distribuído",
        tel_bess_r1: "SoC médio da frota", tel_bess_r2: "Ciclos hoje", tel_bess_r3: "Reserva de segurança", tel_bess_r3_val: "4.0 h · Frequência 50 Hz",
        tel_grid_brand: "Smart Grid Inter-Ilhas", tel_grid_title: "Orquestração HN AI",
        tel_grid_r1: "Ilhas ligadas", tel_grid_r1_val: "5 / 10 · fase 1", tel_grid_r2: "Latência dispatch", tel_grid_r3: "Uptime grid mensal",
        matrix_eyebrow: "Camada de Processamento · Matriz Energética",
        matrix_title: "Quatro pilares operacionais monitorizados pela HN AI",
        sec_solar: "Produção Solar", sec_solar_status: "Utility-scale · em ramp-up", sec_solar_kpi: "Fator Capacidade",
        sec_bess: "BESS Storage", sec_bess_status: "Frota LFP operacional", sec_bess_kpi: "Disponibilidade",
        sec_ev: "Rede EV · Praia", sec_ev_status: "Fast-charge ativos", sec_ev_kpi: "Utilização Média",
        sec_grid: "Smart Grid Inter-Ilhas", sec_grid_status: "Orquestração HN AI 24/7", sec_grid_kpi: "Frequência Estável",
        forecast_eyebrow: "Camada de Interface · Decisão em Tempo Real",
        forecast_title: "Alertas HN AI e previsão energética inter-ilhas",
        forecast_head: "Previsão Energética",
        col_window: "Janela", col_event: "Evento", col_forecast: "Previsão", col_action: "Ação AI", col_confidence: "Confiança",
        impact_eyebrow: "Impacto Social · Modelo Multicliente",
        impact_title: "HX Academy Cabo Verde & ecossistema de beneficiários",
        academy_eyebrow: "Formação Técnica", academy_title: "HX Academy · Praia",
        academy_lede: "Currículos certificados em instalação fotovoltaica, operação de BESS, comissionamento de estações de carregamento e diagnóstico de EVs. A HannoNexus integra os melhores alunos no HX Install Network e no HX Talent Pool — reduzindo a dependência de mão-de-obra expatriada.",
        academy_photo_tag: "Cohort · Solar & BESS Certification",
        academy_photo_caption: "Jovens cabo-verdianos em formação técnica certificada HannoNexus.",
        mini_students: "Alunos certificados 2026", mini_classes: "Turmas ativas", mini_jobs: "Emprego direto criado",
        cta_title: "O blueprint insular pronto para escalar em toda a CPLP.",
        cta_lede: "A arquitetura HX Cabo Verde é modular: solar + BESS + smart grid + academy replicáveis em São Tomé, Angola, Moçambique, Guiné-Bissau e Timor-Leste — integrados na mesma HannoNexus AI.",
        preview_footer: "Fase 1 · 5 ilhas ligadas · 183 alunos certificados",
        alerts: [
          { time: "há 3 min", title: "Pico de produção solar detetado em Santiago", action: "BESS iniciou carga acelerada (94 kW): reserva para pico noturno na ilha da Boa Vista." },
          { time: "há 9 min", title: "Rebalanceamento inter-ilhas · Santiago → Fogo", action: "HN AI redirecionou 128 kWh via cabo submarino para estabilizar frequência a 50 Hz." },
          { time: "há 31 min", title: "Vento sustentado > 9 m/s em Sal", action: "Turbinas Vestas em plena capacidade (2,4 MW); redução de queima diesel na central térmica confirmada." },
        ],
        forecast: [
          { window: "Hoje 18:00", event: "Pico de consumo · Praia",       forecast: "24.6 MW",  action: "BESS pré-carregado" },
          { window: "Amanhã 12:00", event: "Excedente solar · Santiago",    forecast: "+18%",     action: "Exportar via smart grid" },
          { window: "Semana 25", event: "Manutenção preventiva · Sal WT-04", forecast: "6 h",      action: "Janela às 03:00 (baixo vento)" },
          { window: "Semana 26", event: "Reforço BESS · Boavista",           forecast: "+2 MWh",   action: "Contentor 07 chega dia 12" },
        ],
        clients: [
          { group: "ELECTRA · Utility Nacional", consume: "Energia + Smart Grid" },
          { group: "Hotelaria Insular", consume: "Solar + BESS + EV Fleet" },
          { group: "Municípios Ilhas Rurais", consume: "Microgrids + Formação" },
          { group: "Escolas Técnicas & HX Academy", consume: "Currículo + Talent Pool" },
        ],
      },
    },
  },

  en: {
    brand: { powered_by: "Powered by BSGOTECH", tagline: "Integrated Infrastructure Ecosystem" },
    nav: { home: "Home", hx_energy: "HX Energy", hx_automotive: "HX Automotive", hx_systems: "HX Systems", hx_infrastructure: "HX Infrastructure", hx_academy: "HX Academy", projects: "Projects", contact: "Contact", cta_partnerships: "Strategic Partnerships" },
    search: {
      trigger: "Search", placeholder: "Search pages, projects, ecosystems…",
      empty: "No results", hint_navigate: "Navigate", hint_select: "Open", hint_close: "Close",
      groups: { pages: "Pages", ecosystems: "HX Ecosystems", projects: "Projects", presentations: "Presentations" },
    },
    mou: {
      badge: "MEMORANDUM OF UNDERSTANDING",
      title: "Memorandum of Understanding",
      subtitle: "Building a Strategic Partnership for Sustainable Energy in Cabo Verde and the CPLP.",
      cta_download: "Download MOU", cta_request: "Request Partnership", cta_login: "Partner Login",
      logos_row: "Strategic Partner Ecosystem",
      overview: {
        eyebrow: "Partnership Overview", title: "A long-term", accent: "industrial alliance.",
        cards: [
          { icon: "target",   title: "Mission",             body: "Democratise access to utility-grade solar, storage and electric mobility across the Portuguese-speaking countries via tier-1 OEM technology and local industrialisation." },
          { icon: "eye",      title: "Vision",              body: "Turn Cabo Verde and São Tomé and Príncipe into CPLP reference hubs in renewable energy — with technical, financial and educational autonomy by 2030." },
          { icon: "flag",     title: "Objectives",          body: "Supply, install and operate PV, BESS and smart-grid systems — with technology transfer, skilled employment and certified training." },
          { icon: "compass",  title: "Long-Term Strategy",  body: "Replicable model across all 9 CPLP countries: OEM + private-label + local assembly + academy — orchestrated by the HannoNexus platform." },
        ],
      },
      structure: {
        eyebrow: "Partnership Structure", title: "How the", accent: "HannoNexus network is organised.",
        nodes: {
          root:      { title: "HX Ecosystems | BSGOTECH",       sub: "Operator · Architecture & Governance" },
          hn:        { title: "HannoNexus Solar",                sub: "CPLP commercial platform" },
          network:   { title: "Strategic Partnership Network",   sub: "Manufacturers · Services · Institutions" },
          bluesun:   { title: "Bluesun Solar",                   sub: "Strategic Energy Manufacturing Partner", tag: "Manufacturing" },
          services:  { title: "Commercial Service Partners",     sub: "Digital and business services" },
          dwh:       { title: "Digital Wise Hustle",             sub: "Digital Services Partner", tag: "Digital",
                       services: ["Digital Marketing", "Branding", "Website Development", "Business Growth", "Digital Transformation"],
                       model: "HannoNexus commercialises these services and earns a commercial margin." },
          aaban:     { title: "AABAN Solutions",                 sub: "Business Solutions Partner", tag: "Business",
                       services: ["Business Consulting", "Corporate Solutions", "Project Management", "Strategic Advisory"],
                       model: "HannoNexus commercialises these services and earns a commercial margin." },
        },
      },
      bluesun: {
        eyebrow: "Bluesun Solar", title: "Strategic Energy", accent: "Manufacturing Partner.",
        lede: "Bluesun Solar is HannoNexus' OEM manufacturing partner — photovoltaic modules, inverters and battery systems with certified engineering, technical documentation and international certifications.",
        pills: ["Solar Manufacturing", "OEM", "Private Label", "PV Modules", "Inverters", "Battery Systems", "Technical Support", "Engineering", "Documentation", "Certifications", "Joint Market Expansion"],
        flow: { manufacturer: "Manufacturer", hannonexus: "HannoNexus Solar", customer: "Customer",
                m_desc: "Bluesun · Tier-1 OEM", h_desc: "Commercialisation · Engineering · Service", c_desc: "Governments · Enterprises · Utilities" },
      },
      areas: {
        eyebrow: "Areas of Cooperation", title: "Where the partnership", accent: "creates value.",
        list: [
          { icon: "package",     title: "Equipment Supply",       desc: "Panels, inverters, BESS and cables from certified OEM sources." },
          { icon: "factory",     title: "OEM Manufacturing",       desc: "Specification and manufacturing of co-developed products." },
          { icon: "tag",         title: "Private Label",           desc: "Own HannoNexus portfolio with identity and warranty." },
          { icon: "cpu",         title: "Engineering",             desc: "System design, sizing, simulation and as-built." },
          { icon: "sun",         title: "Solar Projects",          desc: "Turnkey utility-scale, commercial and residential projects." },
          { icon: "graduation",  title: "Technical Training",      desc: "HX Academy · certified installation & O&M curricula." },
          { icon: "wrench",      title: "After Sales Service",     desc: "Authorised service network and genuine spare parts." },
          { icon: "network",     title: "Installation Network",    desc: "Local certified partners in HX Install Network." },
          { icon: "globe",       title: "Digital Services",        desc: "Digital Wise Hustle · marketing, branding, web, growth." },
          { icon: "briefcase",   title: "Business Solutions",      desc: "AABAN Solutions · consulting, PMO, strategic advisory." },
          { icon: "map",         title: "Regional Expansion",      desc: "Model replication in Angola, Mozambique, GB, Timor." },
        ],
      },
      benefits: {
        eyebrow: "Why Partner With Us", title: "Four pillars of", accent: "mutual value.",
        cards: [
          { icon: "trending-up", title: "For HannoNexus",   body: "Access to tier-1 OEM manufacturing, private-label portfolio and go-to-market speed across CPLP." },
          { icon: "layers",      title: "For Bluesun Solar", body: "Structured commercial channel in Lusophone markets, local assembly and technical representation." },
          { icon: "users",       title: "For Customers",     body: "Competitive pricing, local warranty, nearby technical service and certified training." },
          { icon: "flag",        title: "For Cabo Verde",    body: "Skilled employment, reduced energy dependence and real technology transfer." },
        ],
      },
      roadmap: {
        eyebrow: "Roadmap", title: "Six phases.", accent: "One plan.",
        phases: [
          { phase: "Phase 1", title: "MOU Signature",             desc: "Formalisation of the strategic commitment between the parties." },
          { phase: "Phase 2", title: "Company Registration",      desc: "HX Ecosystems | BSGOTECH Lda. · headquartered in Cabo Verde." },
          { phase: "Phase 3", title: "First Solar Container",     desc: "Arrival and customs clearance of the first OEM equipment lot." },
          { phase: "Phase 4", title: "Solar Experience Center",   desc: "Permanent technical showroom & demo in Praia." },
          { phase: "Phase 5", title: "Installer Academy",         desc: "First HX Academy cohort · national certification." },
          { phase: "Phase 6", title: "Regional Expansion",        desc: "Replication in STP, Angola, Mozambique and Guinea-Bissau." },
        ],
      },
      team: {
        eyebrow: "Team", title: "A technical team", accent: "based locally.",
        roles: [
          { role: "CEO & Founder",       icon: "star" },
          { role: "Installation Team",   icon: "hard-hat" },
          { role: "Engineering",         icon: "cpu" },
          { role: "Field Operations",    icon: "map" },
          { role: "Finance",             icon: "line-chart" },
          { role: "Marketing & Strategy", icon: "megaphone" },
          { role: "Technical Partners",  icon: "handshake" },
        ],
      },
      gallery: { eyebrow: "Project Gallery", title: "The work delivered.", accent: "Image by image." },
      docs: {
        eyebrow: "Document Center", title: "Public and", accent: "private documents.",
        public_title: "Public Documents",
        public: [
          { icon: "file-text", label: "MOU PDF" },
          { icon: "book",      label: "Product Catalogue" },
          { icon: "slides",    label: "Institutional Presentation" },
          { icon: "award",     label: "Certificates" },
        ],
        private_title: "Private Partner Portal",
        private: [
          { icon: "file-signature", label: "Contracts" },
          { icon: "receipt",        label: "Proforma Invoice" },
          { icon: "file-code",      label: "Technical Documents" },
          { icon: "briefcase",      label: "Commercial Agreements" },
          { icon: "line-chart",     label: "Reports" },
        ],
        login_hint: "Password-protected access", login_cta: "Enter Portal",
      },
      form: {
        eyebrow: "Request Partnership", title: "Let's", accent: "build together.",
        lede: "Fill out the form and the HannoNexus team will reply within 48 business hours.",
        fields: {
          company: "Company", country: "Country", website: "Website",
          contact: "Contact Person", email: "Email", phone: "Phone",
          type: "Type of Partnership",
          type_options: ["Distributor", "Installer", "Investor", "Technology Partner", "Government", "Business Partner"],
          message: "Message",
        },
        submit: "REQUEST PARTNERSHIP", success: "Request received — reply within 48h.", error: "Could not send. Please try again.",
      },
    },
    hero: {
      subtitle_p1: "Integrated", subtitle_accent: "Infrastructure Ecosystem",
      keywords: ["Energy", "Mobility", "Infrastructure", "Digital Systems"],
      lede: "HannoNexus designs and operates integrated ecosystems spanning renewable energy, electric mobility, smart infrastructure and digital platforms — accelerating sustainable development across the Portuguese-speaking countries.",
      cta_explore: "Explore the Ecosystem", cta_partnerships: "Strategic Partnerships", scroll_hint: "Scroll to discover",
    },
    vision: {
      eyebrow: "The Vision", title: "One Platform.", accent: "One Ecosystem.",
      p: "HX Ecosystems is the operational core of HannoNexus — five integrated layers covering the entire value chain, from energy production to mobility, physical infrastructure, digital platforms and technical training.",
    },
    ecosystems: {
      eyebrow: "HX Ecosystems", title: "The operational core of", accent: "HannoNexus",
      lede: "Five integrated ecosystems. One single platform. One operator.",
      includes: "Includes", learn_more: "Learn more",
      strategic_badge: "Strategic Project",
    },
    eco: {
      "hx-energy": {
        name: "HX HannoNexus Energy", short: "HX Energy", tagline: "Smart energy ecosystem",
        description: "Smart energy for production, storage and distribution. From sun to grid, from panel to charging point — a fully integrated value chain.",
        subs: {
          "HX Solar": "Residential, commercial and utility-scale photovoltaic generation.",
          "HX Core": "Residential, commercial and industrial battery storage (BESS).",
          "HX Pulse": "Real-time digital monitoring, AI and telemetry platform.",
          "HX Charge": "AC/DC EV charging network — cafés, hotels, highway corridors.",
          "HX Install Network": "Certified partner network for installation and maintenance.",
          "HX Academy": "Technical training and certification in solar and storage.",
        },
      },
      "hx-automotive": {
        name: "HX HannoNexus Automotive", short: "HX Automotive", tagline: "Electric mobility ecosystem",
        description: "Full electric mobility — from compact city car to grand tourer, from OBOWAY bicycles to electric motorcycles. Private-label brand designed for Lusophone markets, with local assembly and authorised service.",
        subs: {
          "HX EV": "Private-label electric vehicle portfolio: Nudi, Zuri, Pirys, Mionga, Mionga Cargo, Atlan.",
          "OBOWAY": "Own light-mobility brand: 100% electric e-bikes and motorcycles.",
          "HX Assembly": "Local assembly centres with OEM technology transfer.",
          "HX Battery": "Traction batteries, second-life and vehicular storage systems.",
          "HX Mobility": "Shared-mobility platform and urban services.",
          "HX Fleet": "Smart fleet management with telemetry and route optimisation.",
          "HX Service": "Authorised service network and OEM parts.",
        },
        vehicles: {
          "HX Nudi": { segment: "Compact Urban EV", desc: "Compact electric city car — daily urban mobility, ideal for cities and islands." },
          "HX Zuri": { segment: "Executive Sedan", desc: "Extended-range electric executive sedan — corporate & premium mobility." },
          "HX Pirys": { segment: "Adventure SUV", desc: "Off-road-ready electric SUV for volcanic terrain and coastal roads." },
          "HX Mionga": { segment: "Utility Pickup", desc: "Utility electric pickup — agriculture, public works and service fleets." },
          "HX Mionga Cargo": { segment: "Commercial Van", desc: "Electric cargo van for last-mile logistics and commercial distribution." },
          "HX Atlan": { segment: "Flagship Grand Tourer", desc: "Flagship electric grand tourer — the signature HannoNexus car." },
          "OBOWAY E-Bike": { segment: "Electric Bicycle · OBOWAY", desc: "OBOWAY e-bike — light urban and touristic mobility, ideal for islands and historic cities." },
          "OBOWAY Moto": { segment: "Electric Motorcycle · OBOWAY", desc: "OBOWAY electric motorcycle — agile urban and last-mile delivery mobility with zero emissions." },
        },
      },
      "hx-systems": {
        name: "HX Systems", short: "HX Systems", tagline: "Digital platforms & tech infrastructure",
        description: "The digital layer that powers the entire HannoNexus operation — sovereign cloud, artificial intelligence, data and industrial IoT.",
        subs: {
          "HX Cloud": "Public, hybrid and edge cloud — scalable, sovereign infrastructure.",
          "HX AI": "AI models, private LLMs, computer vision and predictive optimisation.",
          "HX HUB": "Unified digital hub — dashboards, workflows and enterprise integrations.",
          "HX Data": "Data lakes, warehouses and real-time analytics pipelines.",
          "HX IoT": "Industrial IoT platform — sensors, telemetry and device management.",
        },
      },
      "hx-infrastructure": {
        name: "HX Infrastructure", short: "HX Infrastructure", tagline: "Smart infrastructure",
        description: "We build the physical infrastructure of tomorrow's cities — power grids, public lighting, water and distributed storage.",
        subs: {
          "Smart Cities": "Integrated urban management — mobility, energy, security.",
          "Microgrids": "Autonomous power grids for communities and industrial zones.",
          "Water Systems": "Smart water management — desalination, treatment, distribution.",
          "Public Lighting": "LED public lighting with IoT — telemetry, dimming, sensors.",
          "Energy Storage": "Distributed storage for grid stability and resilience.",
        },
      },
      "hx-academy": {
        name: "HX Academy", short: "HX Academy", tagline: "Training & certification",
        description: "We train and certify the professionals who operate the HannoNexus ecosystem — engineering, energy, mobility, software and AI.",
        subs: {
          "Energy": "Installation and operation of solar systems, BESS and smart grids.",
          "Mobility": "EV maintenance, fleet management and charging infrastructure.",
          "Software": "Software engineering, cloud and platform development.",
          "AI": "Applied machine learning, computer vision and operational AI.",
          "Engineering": "Electrical, mechanical and industrial systems engineering.",
        },
      },
      "mionga": {
        name: "MIONGA Beverages", short: "MIONGA", tagline: "Premium lifestyle ecosystem",
        description: "Signature beverages, artisanal Moin | Olá coffee, flagship formats and strategic alliances between Europe (Uetze) and the CPLP — the lifestyle brand of the HannoNexus group.",
        subs: {
          "MIONGA Water":     "Natural mineral water · pH 7.2 · Rich in potassium.",
          "MIONGA Sparkling": "Sparkling mineral water · Refreshing and balanced.",
          "MIONGA Juice":     "100% natural juices · No preservatives.",
          "MIONGA Iced Tea":  "Premium iced tea · Hand-selected ingredients.",
          "MIONGA Coffee":    "Premium blend · 100% Arabica.",
          "Moin | Olá Cafés": "Urban flagship format with Kinderecke, bar and retail.",
        },
      },

    },
    how: {
      eyebrow: "How It Works", title: "From energy to", accent: "real-time decision.", phase: "Phase",
      steps: [
        { step: "Energy Production",     desc: "Solar · Wind · Cogeneration" },
        { step: "Smart Storage",         desc: "Residential · commercial · industrial BESS" },
        { step: "Digital Management",    desc: "HX Cloud · HX Data · HX HUB" },
        { step: "Infrastructure",        desc: "Smart Grid · Microgrids · Public Lighting" },
        { step: "Electric Mobility",     desc: "EV · Fleet · Charging" },
        { step: "AI Monitoring",         desc: "HX Pulse · HX AI · Predictive Maintenance" },
        { step: "Real-Time Analytics",   desc: "Executive dashboards · KPIs · Alerts" },
      ],
    },
    projects: {
      eyebrow: "Strategic Projects", title: "Deployment has started in the", accent: "Portuguese-speaking countries.",
      components: "Ecosystem Components", location: "Location", start: "Start", jobs: "Jobs",
      next_markets: "Next Markets",
      next_markets_desc: "Replication of the integrated HannoNexus model is planned for the remaining CPLP countries from 2030 onwards.",
      cta_title_p1: "Government, institutional investor or", cta_title_accent: "strategic partner?",
      cta_lede: "We operate under BOT, joint-venture, private-label OEM and public concession models.",
      cta_button: "Start Partnership",
      list: {
        "cabo-verde": {
          country: "Cape Verde", focus: "HX Energy", tagline: "Archipelagic energy independence.",
          description: "Full energy ecosystem deployment — solar production, storage, smart distribution and local technical training.",
          highlights: ["Solar Energy", "BESS (Battery Storage)", "Inter-island Smart Grid", "Logistics Hubs", "HX Academy"],
        },
        "sao-tome": {
          country: "São Tomé and Príncipe", focus: "HX Automotive", tagline: "Insular electric mobility.",
          description: "Electric mobility ecosystem with local assembly, charging network, digital fleet management and technical training.",
          highlights: ["Electric Vehicles", "Assembly Centre", "Charging Network", "Digital Platform", "Technical Training"],
        },
        "mionga": {
          country: "Uetze → Cape Verde", focus: "MIONGA Beverages", tagline: "Purity that travels. Quality that stays.",
          description: "Premium lifestyle and hospitality ecosystem — signature beverages, artisanal coffee, flagship formats and strategic alliances between Europe and the CPLP.",
          highlights: ["MIONGA Water · Sparkling · Juice · Iced Tea · Coffee", "Moin | Olá Coffee Connection · Uetze HQ", "Atlantic Route ~3,900 km", "Electric Cargo Fleet · Zero Emissions", "Premium Hospitality & F&B Network"],
          linkLabel: "Explore MIONGA",
        },

      },
    },
    pulse: {
      eyebrow: "Intelligent Platform", title: "HX Pulse",
      lede: "The digital hub that monitors the entire HannoNexus operation in real time — from battery cell to charger, from solar panel to executive dashboard.",
      features: ["Artificial Intelligence", "Executive Dashboards", "Industrial Telemetry", "Predictive Maintenance", "24/7 Monitoring", "Starlink Connectivity"],
      chart_label: "Energy Flow · 24h", chart_status: "Real-time",
      metrics: [
        { k: "Solar Yield", v: "12.4 MW", sub: "+8.2%" },
        { k: "ESS SoC", v: "84%", sub: "Nominal" },
        { k: "EV Sessions", v: "1,247", sub: "Today" },
        { k: "CO₂ Avoided", v: "42.1 t", sub: "This month" },
      ],
    },
    impact: {
      eyebrow: "Impact", title: "Numbers that", accent: "measure the future.",
      stats: [
        { value: "20,000+", label: "Systems installed" },
        { value: "60 MW",   label: "Clean energy" },
        { value: "500+",    label: "Certified professionals" },
        { value: "300+",    label: "Qualified jobs" },
        { value: "15,000 t", label: "CO₂ avoided" },
      ],
    },
    roadmap: {
      eyebrow: "Roadmap", title: "Five years.", accent: "Five milestones.",
      items: [
        { year: "2026", milestone: "Strategic Planning",             desc: "Financial modelling, partnerships, permits, preliminary engineering." },
        { year: "2027", milestone: "Logistics Hub & HX Academy",     desc: "Opening of the first logistics hub and training campus." },
        { year: "2028", milestone: "HX Energy Operations",           desc: "Commissioning of solar, BESS and smart grid in Cape Verde." },
        { year: "2029", milestone: "HX Automotive Operations",       desc: "Start of local EV assembly and charging network in STP." },
        { year: "2030", milestone: "Regional Expansion",             desc: "Angola, Mozambique, Guinea-Bissau — integrated model replication." },
      ],
    },
    partners: {
      eyebrow: "Strategic Partners", title: "Built with", accent: "institutions that build nations.",
      categories: ["Governments", "OEM Manufacturers", "Financial Institutions", "ESG Funds", "Technology Partners"],
    },
    investment: {
      eyebrow: "Investment Model", title: "OEM +", accent: "Private Label.",
      lede: "HannoNexus integrates Tier-1 international OEM technology with local assembly, local operations and local service. It is a proven model that transfers tangible value to the host country without compromising industrial quality.",
      advantages: ["Local industrialisation", "Technology transfer", "Operational cost reduction", "Skilled job creation", "Regional scalability"],
    },
    about: {
      eyebrow: "About HannoNexus", title: "A platform dedicated to the", accent: "Portuguese-speaking countries.",
      p: "HannoNexus is a platform dedicated to developing energy, mobility and smart infrastructure ecosystems, delivering technology solutions tailored to the needs of the Portuguese-speaking countries — Cape Verde, São Tomé and Príncipe, Angola, Mozambique, Guinea-Bissau, Timor-Leste.",
    },
    contact_banner: {
      title: "Let's Build the", accent: "Future Together.",
      lede: "Governments, institutional investors, OEM manufacturers and technology partners — we respond within 48 hours.",
      cta: "Strategic Partnerships",
    },
    people: {
      eyebrow: "Our Teams", title: "Built by people.", accent: "Operated in the Lusophone countries.",
      lede: "HannoNexus is operated by local engineers, technicians and trainers — from Cape Verde, São Tomé and Príncipe, and the Lusophone diaspora. Certified training, skilled employment, pride in the work delivered.",
      roles: {
        role_solar: "Solar Technician · HX Install Network",
        role_control: "Operations Engineer · HX Pulse",
        role_mechanic: "EV Technician · HX Service",
        role_academy: "Trainees · HX Academy",
        role_network: "HX Install Network Team",
        role_engineer: "Senior Engineer · HX Energy",
      },
    },
    eco_page: {
      portfolio_eyebrow: "Portfolio", portfolio_title: "What makes up",
      lineup_eyebrow: "Private Label · Vehicle Lineup", lineup_title: "The", lineup_accent: "HannoNexus fleet.",
      lineup_lede: "Complete private-label portfolio: six HX electric cars plus two OBOWAY light-mobility vehicles — from compact city car to flagship grand tourer, from urban e-bike to electric motorcycle.",
      private_label_electric: "Private-label · 100% electric", model: "Model",
      values: [
        { k: "Integrated", v: "A single operational platform covers the entire value chain." },
        { k: "Scalable", v: "Replicable model — from a local pilot to continental expansion." },
        { k: "Certified", v: "TÜV · CE · IEC hardware from tier-1 OEM partners." },
      ],
      others_eyebrow: "Other Ecosystems", others_title: "Explore the rest of the HannoNexus platform.",
      cta_title: "Interested in deploying", cta_lede: "We prepare a tailored deployment plan within 48 hours.",
      cta_button: "Talk to the team",
    },
    footer: {
      tagline_l1: "Energy · Mobility · Infrastructure ·", tagline_l2: "Digital Systems.",
      contact: "Contact", operator: "Operator", operated_by: "Integrated & operated by",
      operator_name: "BSGOTECH Infrastructure Systems",
      ecosystems: "HX Ecosystems", markets: "Strategic Markets",
      copyright: "HannoNexus · Integrated Infrastructure Ecosystem",
      architected: "Architected in Germany", cplp_badge: "CPLP · Powered by BSGOTECH",
    },
    dashboards: {
      common: {
        online: "System Online & Automated", live_dashboard: "Live Dashboard",
        open_dashboard: "Open Live Dashboard", request_demo: "Request demonstration",
        request_meeting: "Request Executive Meeting", other_projects: "View other projects",
        recent_alerts: "Recent Alerts", live_feed: "HN AI · Live Feed",
        ai_recommended: "Recommended by HN AI", client_ecosystem: "Client Ecosystem",
        who_consumes: "Who consumes what", replicability: "Replicability",
        preview_telemetry: "Preview · Live Telemetry", grid_telemetry: "Preview · Grid Telemetry",
      },
      biopinto: {
        country_label: "São Tomé and Príncipe · Pilot Project",
        title_a: "BioPintoSTP", title_b: "Connected Model Farm",
        lede: "Real-time monitoring via the HannoNexus Platform (HN AI) with integrated IoT sensors and CropWatch satellite imagery — an operational digital twin for precision agriculture in São Tomé and Príncipe.",
        featured_lede: "HannoNexus' first integrated agricultural digital twin: real-time IoT monitoring, CropWatch satellite analysis, HN AI-driven decisions and a blueprint replicable across every CPLP country.",
        featured_tag: "IoT · Satellite · HN AI",
        kpi_solar: "Solar Generation", kpi_solar_sub: "Huawei FusionSolar · realtime",
        kpi_water: "Water Savings", kpi_water_sub: "vs. manual irrigation (current month)",
        kpi_nodes: "Active Sensors", kpi_nodes_sub: "IoT nodes online / 130 total",
        kpi_sat: "Satellite Passes", kpi_sat_sub: "CropWatch · NDVI + Thermal",
        loop_eyebrow: "Physical Layer · Closed Loop",
        loop_title: "Solar power → Pumping → Irrigation → Return",
        loop_badge: "Physical Loop · Solar → Pump → Irrigation",
        loop_overlay_title: "Integrated energy ↔ water flow",
        loop_overlay_sub: "Huawei FusionSolar · DIFFUL · Haishun · Zero diesel.",
        tel_solar_brand: "Huawei FusionSolar", tel_solar_title: "Photovoltaic Generation",
        tel_solar_row1: "Current output", tel_solar_row2: "Diesel consumption", tel_solar_row2_val: "0 L · Zero Diesel", tel_solar_row3: "Self-consumption today",
        tel_water_brand: "DIFFUL Pumping & Haishun Irrigation", tel_water_title: "Water Cycle",
        tel_water_row1: "Water flow", tel_water_row1_val: "Optimised (AI-loop)", tel_water_row2: "Accumulated savings", tel_water_row3: "Reservoir volume",
        matrix_eyebrow: "Processing Layer · IoT Matrix",
        matrix_title: "Four production sectors monitored by HN AI",
        sec_avi: "Poultry", sec_avi_status: "Steady cash flow", sec_avi_kpi: "Protein Security",
        sec_viv: "Plant Nursery", sec_viv_status: "Local regeneration active", sec_viv_kpi: "Capacity Utilised",
        sec_flo: "Ornamental Flowers", sec_flo_status: "Focus: high-margin export", sec_flo_kpi: "Ready Batches",
        sec_hor: "Horticulture & Fruit", sec_hor_status: "Smart irrigation active", sec_hor_kpi: "Soil Moisture",
        forecast_eyebrow: "Interface Layer · Real-Time Decision",
        forecast_title: "HN AI alerts and harvest forecast",
        forecast_head: "Harvest Forecast",
        col_window: "Window", col_crop: "Crop", col_volume: "Volume", col_channel: "Channel", col_confidence: "Confidence",
        impact_eyebrow: "Social Impact · Multi-client Model",
        impact_title: "Field Days & beneficiary ecosystem",
        academy_eyebrow: "Field Training", academy_title: "BioPintoSTP Field Days",
        academy_lede: "Quarterly hands-on sessions at the model farm where local farmers, agricultural school students and municipal representatives receive training in smart irrigation, photovoltaic management and IoT data literacy. HannoNexus certifies every participant and onboards top talent into the HX Academy Talent Pool.",
        academy_photo_tag: "Quarterly Session · Sector C",
        academy_photo_caption: "Local farmers and students being trained on smart irrigation and IoT data reading.",
        mini_participants: "Participants 2026", mini_sessions: "Sessions delivered", mini_schools: "Schools engaged",
        cta_title: "A model designed to scale across every CPLP country.",
        cta_lede: "The BioPintoSTP architecture is modular: energy, water, production and training replicable in Cape Verde, Angola, Mozambique and Guinea-Bissau, all integrated within the same HannoNexus AI.",
        alerts: [
          { time: "4 min ago", title: "Climate Alert — Drought detected by CropWatch satellite", action: "Haishun irrigation auto-adjusted: -18% water consumption in sectors B3/B4." },
          { time: "12 min ago", title: "Solar peak above seasonal average (+22%)", action: "Surplus redirected to DIFFUL pumping — water stored in the upper cistern." },
          { time: "38 min ago", title: "Soil moisture — Horticulture C1 at ideal level", action: "Irrigation cycle paused; estimated 340 L savings over next 6 hours." },
        ],
        harvest: [
          { window: "Week 24", crop: "Batavia Lettuce (Sector C2)", volume: "180 kg", channel: "Local Market · STP-South" },
          { window: "Week 26", crop: "Ornamental Flowers — Anthuriums", volume: "12 batches", channel: "Export · Lisbon (LIS)" },
          { window: "Week 27", crop: "Cherry Tomato (Sector C4)", volume: "240 kg", channel: "Hotel Chain · São Tomé" },
          { window: "Week 28", crop: "Free-range Eggs (Poultry A1)", volume: "3,400 un.", channel: "Continuous Distribution" },
        ],
        clients: [
          { group: "Local Farmers", consume: "Technical Training + Production" },
          { group: "Schools & Universities", consume: "Field Days + IoT Curriculum" },
          { group: "Municipalities & Government", consume: "Replicable Model + Data" },
          { group: "NGOs & Cooperation", consume: "Technology + Regeneration" },
        ],
      },
      cv: {
        country_label: "Cape Verde · 10 Islands · Phase 1: 5 Active",
        title_a: "HX Cape Verde", title_b: "Insular Energy Hub",
        status_badge: "Insular Grid · Stabilised",
        featured_status: "Live Grid · Insular Stabilised",
        featured_tag: "Solar · BESS · Smart Grid",
        lede: "Archipelagic energy independence designed and operated by HannoNexus (HN AI) — utility-scale solar production, BESS LFP storage, inter-island smart distribution and local technical training.",
        featured_lede: "Archipelagic energy independence: utility-scale solar production, BESS LFP storage, inter-island smart distribution orchestrated by HN AI and HX Academy to train local talent.",
        kpi_solar: "Solar Installed", kpi_solar_sub: "Santiago · Sal · Boa Vista",
        kpi_bess: "BESS Storage", kpi_bess_sub: "LFP · 261 kWh / module",
        kpi_wind: "Active Wind", kpi_wind_sub: "Sal · Vestas V90",
        kpi_diesel: "Diesel Displaced", kpi_diesel_sub: "vs. 2024 baseline",
        loop_eyebrow: "Physical Layer · Energy Loop",
        loop_title: "Utility-scale solar → BESS → Inter-island smart grid",
        loop_badge: "Loop · Solar → BESS → Smart Grid",
        loop_overlay_title: "Integrated insular energy flow",
        loop_overlay_sub: "Huawei FusionSolar · CATL LFP · Vestas · HN AI orchestration.",
        tel_solar_brand: "Huawei FusionSolar · Utility-Scale", tel_solar_title: "Photovoltaic Production",
        tel_solar_r1: "Instant generation", tel_solar_r2: "Self-consumption today", tel_solar_r3: "Exported surplus",
        tel_bess_brand: "BESS LFP · CATL 261 kWh", tel_bess_title: "Distributed Storage",
        tel_bess_r1: "Fleet average SoC", tel_bess_r2: "Cycles today", tel_bess_r3: "Safety reserve", tel_bess_r3_val: "4.0 h · 50 Hz Frequency",
        tel_grid_brand: "Inter-Island Smart Grid", tel_grid_title: "HN AI Orchestration",
        tel_grid_r1: "Islands connected", tel_grid_r1_val: "5 / 10 · phase 1", tel_grid_r2: "Dispatch latency", tel_grid_r3: "Monthly grid uptime",
        matrix_eyebrow: "Processing Layer · Energy Matrix",
        matrix_title: "Four operational pillars monitored by HN AI",
        sec_solar: "Solar Generation", sec_solar_status: "Utility-scale · ramping up", sec_solar_kpi: "Capacity Factor",
        sec_bess: "BESS Storage", sec_bess_status: "LFP fleet operational", sec_bess_kpi: "Availability",
        sec_ev: "EV Network · Praia", sec_ev_status: "Fast-charge active", sec_ev_kpi: "Average Utilisation",
        sec_grid: "Inter-Island Smart Grid", sec_grid_status: "HN AI orchestration 24/7", sec_grid_kpi: "Stable Frequency",
        forecast_eyebrow: "Interface Layer · Real-Time Decision",
        forecast_title: "HN AI alerts and inter-island energy forecast",
        forecast_head: "Energy Forecast",
        col_window: "Window", col_event: "Event", col_forecast: "Forecast", col_action: "AI Action", col_confidence: "Confidence",
        impact_eyebrow: "Social Impact · Multi-client Model",
        impact_title: "HX Academy Cape Verde & beneficiary ecosystem",
        academy_eyebrow: "Technical Training", academy_title: "HX Academy · Praia",
        academy_lede: "Certified curricula in photovoltaic installation, BESS operation, EV charging commissioning and vehicle diagnostics. HannoNexus onboards the top graduates into the HX Install Network and HX Talent Pool — reducing dependence on expatriate labour.",
        academy_photo_tag: "Cohort · Solar & BESS Certification",
        academy_photo_caption: "Young Cape Verdeans in HannoNexus-certified technical training.",
        mini_students: "Certified students 2026", mini_classes: "Active cohorts", mini_jobs: "Direct jobs created",
        cta_title: "The insular blueprint ready to scale across the CPLP.",
        cta_lede: "The HX Cape Verde architecture is modular: solar + BESS + smart grid + academy replicable in São Tomé, Angola, Mozambique, Guinea-Bissau and Timor-Leste — all integrated within the same HannoNexus AI.",
        preview_footer: "Phase 1 · 5 islands connected · 183 certified students",
        alerts: [
          { time: "3 min ago", title: "Solar peak production detected in Santiago", action: "BESS started accelerated charge (94 kW): reserve for the Boa Vista evening peak." },
          { time: "9 min ago", title: "Inter-island rebalance · Santiago → Fogo", action: "HN AI redirected 128 kWh via submarine cable to stabilise frequency at 50 Hz." },
          { time: "31 min ago", title: "Sustained wind > 9 m/s on Sal", action: "Vestas turbines at full capacity (2.4 MW); confirmed reduction of diesel burn at the thermal plant." },
        ],
        forecast: [
          { window: "Today 18:00", event: "Consumption peak · Praia", forecast: "24.6 MW", action: "BESS pre-charged" },
          { window: "Tomorrow 12:00", event: "Solar surplus · Santiago", forecast: "+18%", action: "Export via smart grid" },
          { window: "Week 25", event: "Preventive maintenance · Sal WT-04", forecast: "6 h", action: "Window at 03:00 (low wind)" },
          { window: "Week 26", event: "BESS reinforcement · Boavista", forecast: "+2 MWh", action: "Container 07 arrives on the 12th" },
        ],
        clients: [
          { group: "ELECTRA · National Utility", consume: "Energy + Smart Grid" },
          { group: "Island Hospitality", consume: "Solar + BESS + EV Fleet" },
          { group: "Rural Island Municipalities", consume: "Microgrids + Training" },
          { group: "Technical Schools & HX Academy", consume: "Curriculum + Talent Pool" },
        ],
      },
    },
  },

  de: {
    brand: { powered_by: "Powered by BSGOTECH", tagline: "Integrated Infrastructure Ecosystem" },
    nav: { home: "Home", hx_energy: "HX Energy", hx_automotive: "HX Automotive", hx_systems: "HX Systems", hx_infrastructure: "HX Infrastructure", hx_academy: "HX Academy", projects: "Projekte", contact: "Kontakt", cta_partnerships: "Strategische Partnerschaften" },
    search: {
      trigger: "Suchen", placeholder: "Seiten, Projekte, Ökosysteme durchsuchen…",
      empty: "Keine Ergebnisse", hint_navigate: "Navigieren", hint_select: "Öffnen", hint_close: "Schließen",
      groups: { pages: "Seiten", ecosystems: "HX Ökosysteme", projects: "Projekte", presentations: "Präsentationen" },
    },
    mou: {
      badge: "MEMORANDUM OF UNDERSTANDING",
      title: "Memorandum of Understanding",
      subtitle: "Aufbau einer strategischen Partnerschaft für nachhaltige Energie in Kap Verde und der CPLP.",
      cta_download: "MOU herunterladen", cta_request: "Partnerschaft anfragen", cta_login: "Partner-Login",
      logos_row: "Strategisches Partner-Ökosystem",
      overview: {
        eyebrow: "Partnerschafts-Überblick", title: "Eine langfristige", accent: "industrielle Allianz.",
        cards: [
          { icon: "target",   title: "Mission",              body: "Zugang zu Utility-Solar, Speichern und Elektromobilität in den portugiesischsprachigen Ländern demokratisieren — durch Tier-1-OEM-Technologie und lokale Industrialisierung." },
          { icon: "eye",      title: "Vision",               body: "Kap Verde und São Tomé und Príncipe bis 2030 zu CPLP-Referenzzentren für erneuerbare Energie machen — mit technischer, finanzieller und bildungsbezogener Autonomie." },
          { icon: "flag",     title: "Ziele",                body: "PV-, BESS- und Smart-Grid-Systeme liefern, installieren und betreiben — mit Technologietransfer, qualifizierter Beschäftigung und zertifizierter Ausbildung." },
          { icon: "compass",  title: "Langfriststrategie",   body: "Replizierbares Modell in allen 9 CPLP-Ländern: OEM + Private-Label + lokale Montage + Academy — orchestriert durch die HannoNexus-Plattform." },
        ],
      },
      structure: {
        eyebrow: "Partnerschaftsstruktur", title: "Wie das", accent: "HannoNexus-Netzwerk organisiert ist.",
        nodes: {
          root:      { title: "HX Ecosystems | BSGOTECH",       sub: "Betreiber · Architektur & Governance" },
          hn:        { title: "HannoNexus Solar",                sub: "CPLP-Vertriebsplattform" },
          network:   { title: "Strategisches Partnernetzwerk",   sub: "Hersteller · Dienste · Institutionen" },
          bluesun:   { title: "Bluesun Solar",                   sub: "Strategischer Fertigungspartner Energie", tag: "Manufacturing" },
          services:  { title: "Kommerzielle Servicepartner",     sub: "Digitale und geschäftliche Dienste" },
          dwh:       { title: "Digital Wise Hustle",             sub: "Partner für digitale Dienste", tag: "Digital",
                       services: ["Digitales Marketing", "Branding", "Webentwicklung", "Business Growth", "Digitale Transformation"],
                       model: "HannoNexus vermarktet diese Dienste und erzielt eine Handelsmarge." },
          aaban:     { title: "AABAN Solutions",                 sub: "Partner für Unternehmenslösungen", tag: "Business",
                       services: ["Unternehmensberatung", "Corporate Solutions", "Projektmanagement", "Strategische Beratung"],
                       model: "HannoNexus vermarktet diese Dienste und erzielt eine Handelsmarge." },
        },
      },
      bluesun: {
        eyebrow: "Bluesun Solar", title: "Strategischer", accent: "Fertigungspartner Energie.",
        lede: "Bluesun Solar ist der OEM-Fertigungspartner von HannoNexus — Photovoltaikmodule, Wechselrichter und Batteriesysteme mit zertifiziertem Engineering, technischer Dokumentation und internationalen Zertifizierungen.",
        pills: ["Solar-Fertigung", "OEM", "Private Label", "PV-Module", "Wechselrichter", "Batteriesysteme", "Technischer Support", "Engineering", "Dokumentation", "Zertifizierungen", "Gemeinsame Markterweiterung"],
        flow: { manufacturer: "Hersteller", hannonexus: "HannoNexus Solar", customer: "Endkunde",
                m_desc: "Bluesun · Tier-1 OEM", h_desc: "Vertrieb · Engineering · Service", c_desc: "Regierungen · Unternehmen · Utility" },
      },
      areas: {
        eyebrow: "Kooperationsbereiche", title: "Wo die Partnerschaft", accent: "Wert schafft.",
        list: [
          { icon: "package",     title: "Ausrüstungslieferung",  desc: "Panels, Wechselrichter, BESS und Kabel aus zertifizierten OEM-Quellen." },
          { icon: "factory",     title: "OEM-Fertigung",         desc: "Spezifikation und Fertigung gemeinsam entwickelter Produkte." },
          { icon: "tag",         title: "Private Label",         desc: "Eigenes HannoNexus-Portfolio mit Identität und Garantie." },
          { icon: "cpu",         title: "Engineering",           desc: "Systemauslegung, Dimensionierung, Simulation und As-Built." },
          { icon: "sun",         title: "Solarprojekte",         desc: "Schlüsselfertige Utility-, Gewerbe- und Wohnprojekte." },
          { icon: "graduation",  title: "Technische Ausbildung", desc: "HX Academy · zertifizierte Installations- & O&M-Curricula." },
          { icon: "wrench",      title: "After-Sales-Service",   desc: "Autorisiertes Servicenetzwerk und Original-Ersatzteile." },
          { icon: "network",     title: "Installationsnetzwerk", desc: "Lokal zertifizierte Partner im HX Install Network." },
          { icon: "globe",       title: "Digitale Dienste",      desc: "Digital Wise Hustle · Marketing, Branding, Web, Growth." },
          { icon: "briefcase",   title: "Unternehmenslösungen",  desc: "AABAN Solutions · Beratung, PMO, strategisches Advisory." },
          { icon: "map",         title: "Regionale Expansion",   desc: "Modellreplikation in Angola, Mosambik, GB, Timor." },
        ],
      },
      benefits: {
        eyebrow: "Warum Partnerschaft", title: "Vier Säulen", accent: "gegenseitigen Werts.",
        cards: [
          { icon: "trending-up", title: "Für HannoNexus",    body: "Zugang zu Tier-1-OEM-Fertigung, Private-Label-Portfolio und Go-to-Market-Geschwindigkeit in der CPLP." },
          { icon: "layers",      title: "Für Bluesun Solar", body: "Strukturierter Vertriebskanal in lusophonen Märkten, lokale Montage und technische Vertretung." },
          { icon: "users",       title: "Für Kunden",        body: "Wettbewerbsfähige Preise, lokale Garantie, nahe technischer Service und zertifizierte Ausbildung." },
          { icon: "flag",        title: "Für Kap Verde",     body: "Qualifizierte Beschäftigung, reduzierte Energieabhängigkeit und echter Technologietransfer." },
        ],
      },
      roadmap: {
        eyebrow: "Roadmap", title: "Sechs Phasen.", accent: "Ein Plan.",
        phases: [
          { phase: "Phase 1", title: "MOU-Unterzeichnung",       desc: "Formalisierung des strategischen Engagements zwischen den Parteien." },
          { phase: "Phase 2", title: "Unternehmensregistrierung", desc: "HX Ecosystems | BSGOTECH Lda. · Sitz in Kap Verde." },
          { phase: "Phase 3", title: "Erster Solar-Container",   desc: "Ankunft und Zollabfertigung der ersten OEM-Ausrüstungscharge." },
          { phase: "Phase 4", title: "Solar Experience Center",  desc: "Permanenter technischer Showroom & Demo in Praia." },
          { phase: "Phase 5", title: "Installer Academy",        desc: "Erste HX Academy-Kohorte · nationale Zertifizierung." },
          { phase: "Phase 6", title: "Regionale Expansion",      desc: "Replikation in STP, Angola, Mosambik und Guinea-Bissau." },
        ],
      },
      team: {
        eyebrow: "Team", title: "Ein technisches Team", accent: "vor Ort.",
        roles: [
          { role: "CEO & Gründer",           icon: "star" },
          { role: "Installationsteam",       icon: "hard-hat" },
          { role: "Engineering",             icon: "cpu" },
          { role: "Feldoperationen",         icon: "map" },
          { role: "Finanzen",                icon: "line-chart" },
          { role: "Marketing & Strategie",   icon: "megaphone" },
          { role: "Technische Partner",      icon: "handshake" },
        ],
      },
      gallery: { eyebrow: "Projektgalerie", title: "Die geleistete Arbeit.", accent: "Bild für Bild." },
      docs: {
        eyebrow: "Dokumentenzentrum", title: "Öffentliche und", accent: "private Dokumente.",
        public_title: "Öffentliche Dokumente",
        public: [
          { icon: "file-text", label: "MOU PDF" },
          { icon: "book",      label: "Produktkatalog" },
          { icon: "slides",    label: "Institutionelle Präsentation" },
          { icon: "award",     label: "Zertifikate" },
        ],
        private_title: "Privates Partnerportal",
        private: [
          { icon: "file-signature", label: "Verträge" },
          { icon: "receipt",        label: "Pro-forma-Rechnung" },
          { icon: "file-code",      label: "Technische Dokumente" },
          { icon: "briefcase",      label: "Kommerzielle Vereinbarungen" },
          { icon: "line-chart",     label: "Berichte" },
        ],
        login_hint: "Passwortgeschützter Zugang", login_cta: "Portal betreten",
      },
      form: {
        eyebrow: "Partnerschaft anfragen", title: "Lass uns", accent: "gemeinsam bauen.",
        lede: "Füllen Sie das Formular aus und das HannoNexus-Team antwortet innerhalb von 48 Werkstunden.",
        fields: {
          company: "Unternehmen", country: "Land", website: "Website",
          contact: "Ansprechpartner", email: "E-Mail", phone: "Telefon",
          type: "Art der Partnerschaft",
          type_options: ["Distributor", "Installateur", "Investor", "Technologiepartner", "Regierung", "Geschäftspartner"],
          message: "Nachricht",
        },
        submit: "PARTNERSCHAFT ANFRAGEN", success: "Anfrage erhalten — Antwort innerhalb von 48 Stunden.", error: "Senden fehlgeschlagen. Bitte erneut versuchen.",
      },
    },
    hero: {
      subtitle_p1: "Integrated", subtitle_accent: "Infrastructure Ecosystem",
      keywords: ["Energie", "Mobilität", "Infrastruktur", "Digital Systems"],
      lede: "HannoNexus konzipiert und betreibt integrierte Ökosysteme, die erneuerbare Energie, Elektromobilität, intelligente Infrastruktur und digitale Plattformen verbinden — und beschleunigt damit die nachhaltige Entwicklung der portugiesischsprachigen Länder.",
      cta_explore: "Ökosystem erkunden", cta_partnerships: "Strategische Partnerschaften", scroll_hint: "Nach unten scrollen",
    },
    vision: {
      eyebrow: "Die Vision", title: "Eine Plattform.", accent: "Ein Ökosystem.",
      p: "HX Ecosystems ist der operative Kern von HannoNexus — fünf integrierte Ebenen, die die gesamte Wertschöpfungskette abdecken: von der Energieerzeugung über Mobilität, physische Infrastruktur und digitale Plattformen bis hin zur technischen Ausbildung.",
    },
    ecosystems: {
      eyebrow: "HX Ecosystems", title: "Der operative Kern von", accent: "HannoNexus",
      lede: "Fünf integrierte Ökosysteme. Eine einzige Plattform. Ein Betreiber.",
      includes: "Enthält", learn_more: "Mehr erfahren",
      strategic_badge: "Strategisches Projekt",
    },
    eco: {
      "hx-energy": {
        name: "HX HannoNexus Energy", short: "HX Energy", tagline: "Intelligentes Energieökosystem",
        description: "Intelligente Energie für Produktion, Speicherung und Verteilung. Von der Sonne zum Netz, vom Panel zum Ladepunkt — eine vollständig integrierte Wertschöpfungskette.",
        subs: {
          "HX Solar": "Photovoltaik-Erzeugung für Wohn-, Gewerbe- und Utility-Bereich.",
          "HX Core": "Batteriespeicher (BESS) für Wohn-, Gewerbe- und Industriebereich.",
          "HX Pulse": "Echtzeit-Plattform für Monitoring, KI und Telemetrie.",
          "HX Charge": "AC/DC-Ladeinfrastruktur für EV — Cafés, Hotels, Autobahnkorridore.",
          "HX Install Network": "Zertifiziertes Partnernetzwerk für Installation und Wartung.",
          "HX Academy": "Technische Ausbildung und Zertifizierung für Solar und Speicher.",
        },
      },
      "hx-automotive": {
        name: "HX HannoNexus Automotive", short: "HX Automotive", tagline: "Elektromobilitäts-Ökosystem",
        description: "Vollständige Elektromobilität — vom kompakten Stadtwagen bis zum Grand Tourer, von OBOWAY-Fahrrädern bis zu Elektromotorrädern. Private-Label-Marke für die portugiesischsprachigen Märkte mit lokaler Montage und autorisiertem Service.",
        subs: {
          "HX EV": "Private-Label-Portfolio elektrischer Fahrzeuge: Nudi, Zuri, Pirys, Mionga, Mionga Cargo, Atlan.",
          "OBOWAY": "Eigene Marke für leichte Elektromobilität: 100% elektrische E-Bikes und Motorräder.",
          "HX Assembly": "Lokale Montagezentren mit OEM-Technologietransfer.",
          "HX Battery": "Traktionsbatterien, Second-Life und Fahrzeugspeichersysteme.",
          "HX Mobility": "Shared-Mobility-Plattform und städtische Dienste.",
          "HX Fleet": "Intelligentes Flottenmanagement mit Telemetrie und Routenoptimierung.",
          "HX Service": "Autorisiertes Servicenetzwerk und OEM-Teile.",
        },
        vehicles: {
          "HX Nudi": { segment: "Compact Urban EV", desc: "Kompaktes elektrisches Stadtauto — tägliche urbane Mobilität, ideal für Städte und Inseln." },
          "HX Zuri": { segment: "Executive Sedan", desc: "Elektrische Executive-Limousine mit erweiterter Reichweite — Corporate & Premium Mobility." },
          "HX Pirys": { segment: "Adventure SUV", desc: "Off-road-tauglicher elektrischer SUV für vulkanisches Gelände und Küstenstraßen." },
          "HX Mionga": { segment: "Utility Pickup", desc: "Elektrischer Utility-Pickup — Landwirtschaft, öffentliche Bauten und Servicefuhrparks." },
          "HX Mionga Cargo": { segment: "Commercial Van", desc: "Elektrischer Cargo-Van für Last-Mile-Logistik und gewerbliche Distribution." },
          "HX Atlan": { segment: "Flagship Grand Tourer", desc: "Flaggschiff-Elektro-Grand-Tourer — das Signature-Fahrzeug von HannoNexus." },
          "OBOWAY E-Bike": { segment: "Electric Bicycle · OBOWAY", desc: "OBOWAY E-Bike — leichte urbane und touristische Mobilität, ideal für Inseln und historische Städte." },
          "OBOWAY Moto": { segment: "Electric Motorcycle · OBOWAY", desc: "OBOWAY Elektromotorrad — agile urbane und Last-Mile-Delivery-Mobilität mit null Emissionen." },
        },
      },
      "hx-systems": {
        name: "HX Systems", short: "HX Systems", tagline: "Digitale Plattformen & Technologieinfrastruktur",
        description: "Die digitale Ebene, die den gesamten HannoNexus-Betrieb antreibt — souveräne Cloud, künstliche Intelligenz, Daten und industrielles IoT.",
        subs: {
          "HX Cloud": "Public, Hybrid und Edge Cloud — skalierbare, souveräne Infrastruktur.",
          "HX AI": "KI-Modelle, private LLMs, Computer Vision und prädiktive Optimierung.",
          "HX HUB": "Einheitliches digitales Hub — Dashboards, Workflows und Enterprise-Integrationen.",
          "HX Data": "Data Lakes, Warehouses und Echtzeit-Analytics-Pipelines.",
          "HX IoT": "Industrielle IoT-Plattform — Sensoren, Telemetrie und Geräteverwaltung.",
        },
      },
      "hx-infrastructure": {
        name: "HX Infrastructure", short: "HX Infrastructure", tagline: "Intelligente Infrastruktur",
        description: "Wir bauen die physische Infrastruktur der Städte von morgen — Stromnetze, öffentliche Beleuchtung, Wasser und verteilte Speicher.",
        subs: {
          "Smart Cities": "Integriertes urbanes Management — Mobilität, Energie, Sicherheit.",
          "Microgrids": "Autonome Stromnetze für Gemeinden und Industriezonen.",
          "Water Systems": "Intelligentes Wassermanagement — Entsalzung, Aufbereitung, Verteilung.",
          "Public Lighting": "LED-Straßenbeleuchtung mit IoT — Telemetrie, Dimming, Sensoren.",
          "Energy Storage": "Verteilte Speicher für Netzstabilität und Resilienz.",
        },
      },
      "hx-academy": {
        name: "HX Academy", short: "HX Academy", tagline: "Ausbildung & Zertifizierung",
        description: "Wir bilden die Fachkräfte aus und zertifizieren sie, die das HannoNexus-Ökosystem betreiben — Ingenieurwesen, Energie, Mobilität, Software und KI.",
        subs: {
          "Energy": "Installation und Betrieb von Solaranlagen, BESS und intelligenten Netzen.",
          "Mobility": "EV-Wartung, Flottenmanagement und Ladeinfrastruktur.",
          "Software": "Software-Engineering, Cloud und Plattformentwicklung.",
          "AI": "Angewandtes Machine Learning, Computer Vision und operative KI.",
          "Engineering": "Elektro-, Mechanik- und Industrieanlagen-Engineering.",
        },
      },
      "mionga": {
        name: "MIONGA Beverages", short: "MIONGA", tagline: "Premium-Lifestyle-Ökosystem",
        description: "Signaturgetränke, handwerklicher Kaffee Moin | Olá, Flagship-Formate und strategische Allianzen zwischen Europa (Uetze) und der CPLP — die Lifestyle-Marke der HannoNexus-Gruppe.",
        subs: {
          "MIONGA Water":     "Natürliches Mineralwasser · pH 7,2 · Reich an Kalium.",
          "MIONGA Sparkling": "Mineralwasser mit Kohlensäure · Erfrischend und ausgewogen.",
          "MIONGA Juice":     "100% natürliche Säfte · Ohne Konservierungsstoffe.",
          "MIONGA Iced Tea":  "Premium Eistee · Handverlesene Zutaten.",
          "MIONGA Coffee":    "Premium-Blend · 100% Arabica.",
          "Moin | Olá Cafés": "Urbanes Flagship-Format mit Kinderecke, Bar und Retail.",
        },
      },

    },
    how: {
      eyebrow: "So funktioniert es", title: "Von der Energie zur", accent: "Echtzeit-Entscheidung.", phase: "Phase",
      steps: [
        { step: "Energieerzeugung",      desc: "Solar · Wind · Kraft-Wärme-Kopplung" },
        { step: "Intelligente Speicherung", desc: "BESS für Wohn-, Gewerbe- und Industriebereich" },
        { step: "Digitales Management",  desc: "HX Cloud · HX Data · HX HUB" },
        { step: "Infrastruktur",         desc: "Smart Grid · Microgrids · Öffentliche Beleuchtung" },
        { step: "Elektromobilität",      desc: "EV · Flotte · Charging" },
        { step: "KI-Monitoring",         desc: "HX Pulse · HX AI · Prädiktive Wartung" },
        { step: "Echtzeit-Analytik",     desc: "Executive-Dashboards · KPIs · Alerts" },
      ],
    },
    projects: {
      eyebrow: "Strategische Projekte", title: "Die Umsetzung hat in den", accent: "portugiesischsprachigen Ländern begonnen.",
      components: "Ökosystem-Komponenten", location: "Standort", start: "Start", jobs: "Arbeitsplätze",
      next_markets: "Nächste Märkte",
      next_markets_desc: "Die Replikation des integrierten HannoNexus-Modells ist ab 2030 für die übrigen CPLP-Länder geplant.",
      cta_title_p1: "Regierung, institutioneller Investor oder", cta_title_accent: "strategischer Partner?",
      cta_lede: "Wir arbeiten mit BOT-, Joint-Venture-, Private-Label-OEM- und öffentlichen Konzessionsmodellen.",
      cta_button: "Partnerschaft starten",
      list: {
        "cabo-verde": {
          country: "Kap Verde", focus: "HX Energy", tagline: "Energieunabhängigkeit des Archipels.",
          description: "Aufbau eines vollständigen Energieökosystems — Solarproduktion, Speicherung, intelligente Verteilung und lokale technische Ausbildung.",
          highlights: ["Solarenergie", "BESS (Batteriespeicher)", "Interinsulares Smart Grid", "Logistikzentren", "HX Academy"],
        },
        "sao-tome": {
          country: "São Tomé und Príncipe", focus: "HX Automotive", tagline: "Insulare Elektromobilität.",
          description: "Elektromobilitäts-Ökosystem mit lokaler Montage, Ladenetzwerk, digitalem Flottenmanagement und technischer Ausbildung.",
          highlights: ["Elektrofahrzeuge", "Montagezentrum", "Ladenetzwerk", "Digitale Plattform", "Technische Ausbildung"],
        },
        "mionga": {
          country: "Uetze → Kap Verde", focus: "MIONGA Beverages", tagline: "Reinheit, die reist. Qualität, die bleibt.",
          description: "Premium-Lifestyle- und Gastronomie-Ökosystem — Signaturgetränke, handwerklicher Kaffee, Flagship-Formate und strategische Allianzen zwischen Europa und der CPLP.",
          highlights: ["MIONGA Water · Sparkling · Juice · Iced Tea · Coffee", "Moin | Olá Coffee Connection · Uetze HQ", "Atlantik-Route ~3.900 km", "Elektrische Frachtflotte · Null Emissionen", "Premium-Hotellerie & F&B-Netzwerk"],
          linkLabel: "MIONGA erkunden",
        },
      },
    },
    pulse: {
      eyebrow: "Intelligente Plattform", title: "HX Pulse",
      lede: "Das digitale Zentrum, das den gesamten HannoNexus-Betrieb in Echtzeit überwacht — von der Batteriezelle bis zum Ladegerät, vom Solarpanel bis zum Executive-Dashboard.",
      features: ["Künstliche Intelligenz", "Executive-Dashboards", "Industrielle Telemetrie", "Prädiktive Wartung", "24/7-Überwachung", "Starlink-Konnektivität"],
      chart_label: "Energiefluss · 24h", chart_status: "Echtzeit",
      metrics: [
        { k: "Solar Yield", v: "12,4 MW", sub: "+8,2%" },
        { k: "ESS SoC", v: "84%", sub: "Nominal" },
        { k: "EV Sessions", v: "1.247", sub: "Heute" },
        { k: "CO₂ vermieden", v: "42,1 t", sub: "Diesen Monat" },
      ],
    },
    impact: {
      eyebrow: "Impact", title: "Zahlen, die die", accent: "Zukunft messen.",
      stats: [
        { value: "20.000+", label: "Installierte Systeme" },
        { value: "60 MW",   label: "Saubere Energie" },
        { value: "500+",    label: "Zertifizierte Fachkräfte" },
        { value: "300+",    label: "Qualifizierte Arbeitsplätze" },
        { value: "15.000 t", label: "CO₂ vermieden" },
      ],
    },
    roadmap: {
      eyebrow: "Roadmap", title: "Fünf Jahre.", accent: "Fünf Meilensteine.",
      items: [
        { year: "2026", milestone: "Strategische Planung",             desc: "Finanzmodellierung, Partnerschaften, Genehmigungen, Vorplanung." },
        { year: "2027", milestone: "Logistikzentrum & HX Academy",     desc: "Eröffnung des ersten Logistikzentrums und Ausbildungscampus." },
        { year: "2028", milestone: "HX Energy Betrieb",                desc: "Inbetriebnahme von Solar, BESS und Smart Grid in Kap Verde." },
        { year: "2029", milestone: "HX Automotive Betrieb",            desc: "Beginn der lokalen EV-Montage und des Ladenetzwerks in STP." },
        { year: "2030", milestone: "Regionale Expansion",              desc: "Angola, Mosambik, Guinea-Bissau — Replikation des integrierten Modells." },
      ],
    },
    partners: {
      eyebrow: "Strategische Partner", title: "Gebaut mit", accent: "Institutionen, die Länder aufbauen.",
      categories: ["Regierungen", "OEM-Hersteller", "Finanzinstitutionen", "ESG-Fonds", "Technologiepartner"],
    },
    investment: {
      eyebrow: "Investitionsmodell", title: "OEM +", accent: "Private Label.",
      lede: "HannoNexus integriert internationale Tier-1-OEM-Technologie mit lokaler Montage, lokalem Betrieb und lokalem Service. Ein bewährtes Modell, das Wert an das Zielland überträgt, ohne die industrielle Qualität zu opfern.",
      advantages: ["Lokale Industrialisierung", "Technologietransfer", "Reduktion der Betriebskosten", "Schaffung qualifizierter Arbeitsplätze", "Regionale Skalierbarkeit"],
    },
    about: {
      eyebrow: "Über HannoNexus", title: "Eine Plattform für die", accent: "portugiesischsprachigen Länder.",
      p: "HannoNexus ist eine Plattform zur Entwicklung von Ökosystemen für Energie, Mobilität und intelligente Infrastruktur, die technologische Lösungen anbietet, die auf die Bedürfnisse der portugiesischsprachigen Länder zugeschnitten sind — Kap Verde, São Tomé und Príncipe, Angola, Mosambik, Guinea-Bissau, Osttimor.",
    },
    contact_banner: {
      title: "Lasst uns die", accent: "Zukunft gemeinsam bauen.",
      lede: "Regierungen, institutionelle Investoren, OEM-Hersteller und Technologiepartner — wir antworten innerhalb von 48 Stunden.",
      cta: "Strategische Partnerschaften",
    },
    people: {
      eyebrow: "Unsere Teams", title: "Von Menschen gebaut.", accent: "In den lusophonen Ländern betrieben.",
      lede: "HannoNexus wird von lokalen Ingenieuren, Technikern und Ausbildern betrieben — aus Kap Verde, São Tomé und Príncipe sowie der lusophonen Diaspora. Zertifizierte Ausbildung, qualifizierte Beschäftigung, Stolz auf die geleistete Arbeit.",
      roles: {
        role_solar: "Solartechnikerin · HX Install Network",
        role_control: "Betriebsingenieurin · HX Pulse",
        role_mechanic: "EV-Techniker · HX Service",
        role_academy: "Auszubildende · HX Academy",
        role_network: "HX Install Network Team",
        role_engineer: "Senior-Ingenieurin · HX Energy",
      },
    },
    eco_page: {
      portfolio_eyebrow: "Portfolio", portfolio_title: "Was",
      lineup_eyebrow: "Private Label · Fahrzeugflotte", lineup_title: "Die", lineup_accent: "HannoNexus-Flotte.",
      lineup_lede: "Vollständiges Private-Label-Portfolio: sechs HX-Elektrofahrzeuge plus zwei OBOWAY-Leichtmobilitätsfahrzeuge — vom kompakten Stadtauto bis zum Flaggschiff-Grand-Tourer, vom urbanen E-Bike bis zum Elektromotorrad.",
      private_label_electric: "Private-label · 100% elektrisch", model: "Modell",
      values: [
        { k: "Integriert", v: "Eine einzige operative Plattform deckt die gesamte Wertschöpfungskette ab." },
        { k: "Skalierbar", v: "Replikables Modell — vom lokalen Pilot bis zur kontinentalen Expansion." },
        { k: "Zertifiziert", v: "TÜV · CE · IEC Hardware von Tier-1-OEM-Partnern." },
      ],
      others_eyebrow: "Andere Ökosysteme", others_title: "Entdecken Sie den Rest der HannoNexus-Plattform.",
      cta_title: "Interesse an der Einführung von", cta_lede: "Wir erstellen innerhalb von 48 Stunden einen maßgeschneiderten Umsetzungsplan.",
      cta_button: "Mit dem Team sprechen",
    },
    footer: {
      tagline_l1: "Energie · Mobilität · Infrastruktur ·", tagline_l2: "Digitale Systeme.",
      contact: "Kontakt", operator: "Betreiber", operated_by: "Integriert & betrieben von",
      operator_name: "BSGOTECH Infrastructure Systems",
      ecosystems: "HX Ecosystems", markets: "Strategische Märkte",
      copyright: "HannoNexus · Integrated Infrastructure Ecosystem",
      architected: "Architected in Germany", cplp_badge: "CPLP · Powered by BSGOTECH",
    },
    dashboards: {
      common: {
        online: "System Online & Automatisiert", live_dashboard: "Live-Dashboard",
        open_dashboard: "Live-Dashboard öffnen", request_demo: "Vorführung anfordern",
        request_meeting: "Executive Meeting anfordern", other_projects: "Weitere Projekte ansehen",
        recent_alerts: "Aktuelle Meldungen", live_feed: "HN AI · Live-Feed",
        ai_recommended: "Empfohlen von HN AI", client_ecosystem: "Kunden-Ökosystem",
        who_consumes: "Wer konsumiert was", replicability: "Replizierbarkeit",
        preview_telemetry: "Vorschau · Live-Telemetrie", grid_telemetry: "Vorschau · Netz-Telemetrie",
      },
      biopinto: {
        country_label: "São Tomé und Príncipe · Pilotprojekt",
        title_a: "BioPintoSTP", title_b: "Vernetzter Modellhof",
        lede: "Echtzeit-Überwachung über die HannoNexus-Plattform (HN AI) mit integrierten IoT-Sensoren und CropWatch-Satellitenbildern — ein operativer digitaler Zwilling für Präzisionslandwirtschaft in São Tomé und Príncipe.",
        featured_lede: "Der erste integrierte landwirtschaftliche digitale Zwilling von HannoNexus: Echtzeit-IoT-Überwachung, CropWatch-Satellitenanalyse, HN-AI-gestützte Entscheidungen und eine für alle CPLP-Länder replizierbare Blaupause.",
        featured_tag: "IoT · Satellit · HN AI",
        kpi_solar: "Solar-Erzeugung", kpi_solar_sub: "Huawei FusionSolar · Echtzeit",
        kpi_water: "Wassereinsparung", kpi_water_sub: "vs. manuelle Bewässerung (laufender Monat)",
        kpi_nodes: "Aktive Sensoren", kpi_nodes_sub: "IoT-Nodes online / 130 gesamt",
        kpi_sat: "Satellitenüberflüge", kpi_sat_sub: "CropWatch · NDVI + Thermal",
        loop_eyebrow: "Physische Schicht · Geschlossener Kreislauf",
        loop_title: "Solarstrom → Pumpen → Bewässerung → Rückführung",
        loop_badge: "Physischer Kreislauf · Solar → Pumpe → Bewässerung",
        loop_overlay_title: "Integrierter Energie-Wasser-Fluss",
        loop_overlay_sub: "Huawei FusionSolar · DIFFUL · Haishun · Zero Diesel.",
        tel_solar_brand: "Huawei FusionSolar", tel_solar_title: "Photovoltaische Erzeugung",
        tel_solar_row1: "Aktuelle Leistung", tel_solar_row2: "Dieselverbrauch", tel_solar_row2_val: "0 L · Zero Diesel", tel_solar_row3: "Eigenverbrauch heute",
        tel_water_brand: "DIFFUL Pumpen & Haishun Bewässerung", tel_water_title: "Wasserkreislauf",
        tel_water_row1: "Wasserfluss", tel_water_row1_val: "Optimiert (AI-Loop)", tel_water_row2: "Kumulierte Einsparung", tel_water_row3: "Speichervolumen",
        matrix_eyebrow: "Verarbeitungsschicht · IoT-Matrix",
        matrix_title: "Vier Produktionssektoren, überwacht von HN AI",
        sec_avi: "Geflügelhaltung", sec_avi_status: "Stetiger Cashflow", sec_avi_kpi: "Proteinsicherheit",
        sec_viv: "Pflanzenschule", sec_viv_status: "Lokale Regeneration aktiv", sec_viv_kpi: "Auslastung",
        sec_flo: "Zierblumen", sec_flo_status: "Fokus: margenstarke Exporte", sec_flo_kpi: "Fertige Chargen",
        sec_hor: "Gemüse & Obst", sec_hor_status: "Intelligente Bewässerung aktiv", sec_hor_kpi: "Bodenfeuchte",
        forecast_eyebrow: "Interface-Schicht · Entscheidung in Echtzeit",
        forecast_title: "HN-AI-Meldungen und Ernteprognose",
        forecast_head: "Ernteprognose",
        col_window: "Zeitfenster", col_crop: "Kultur", col_volume: "Volumen", col_channel: "Kanal", col_confidence: "Konfidenz",
        impact_eyebrow: "Sozialer Impact · Multi-Client-Modell",
        impact_title: "Feldtage & Beneficiary-Ökosystem",
        academy_eyebrow: "Feldschulung", academy_title: "BioPintoSTP Feldtage",
        academy_lede: "Vierteljährliche Praxissitzungen auf dem Modellhof, in denen lokale Landwirte, Studierende landwirtschaftlicher Schulen und kommunale Vertreter Schulungen in intelligenter Bewässerung, Photovoltaik-Management und IoT-Datenlesekompetenz erhalten. HannoNexus zertifiziert jeden Teilnehmer und übernimmt Top-Talente in den HX Academy Talent Pool.",
        academy_photo_tag: "Quartalssitzung · Sektor C",
        academy_photo_caption: "Lokale Landwirte und Studierende in Schulung zu intelligenter Bewässerung und IoT-Datenanalyse.",
        mini_participants: "Teilnehmer 2026", mini_sessions: "Durchgeführte Sitzungen", mini_schools: "Beteiligte Schulen",
        cta_title: "Ein Modell, ausgelegt zur Skalierung über alle CPLP-Länder.",
        cta_lede: "Die BioPintoSTP-Architektur ist modular: Energie, Wasser, Produktion und Ausbildung — replizierbar in Kap Verde, Angola, Mosambik und Guinea-Bissau, integriert in dieselbe HannoNexus AI.",
        alerts: [
          { time: "vor 4 Min.", title: "Klimawarnung — Trockenheit vom CropWatch-Satelliten erkannt", action: "Haishun-Bewässerung automatisch angepasst: -18 % Wasserverbrauch in Sektoren B3/B4." },
          { time: "vor 12 Min.", title: "Solarspitze über saisonalem Durchschnitt (+22 %)", action: "Überschuss zur DIFFUL-Pumpe umgeleitet — Wasser im oberen Reservoir gespeichert." },
          { time: "vor 38 Min.", title: "Bodenfeuchte — Gemüsesektor C1 im Idealzustand", action: "Bewässerungszyklus pausiert; geschätzte 340 L Einsparung in den nächsten 6 Stunden." },
        ],
        harvest: [
          { window: "KW 24", crop: "Batavia-Salat (Sektor C2)", volume: "180 kg", channel: "Lokaler Markt · STP-Süd" },
          { window: "KW 26", crop: "Zierblumen — Anthurien", volume: "12 Chargen", channel: "Export · Lissabon (LIS)" },
          { window: "KW 27", crop: "Cherry-Tomate (Sektor C4)", volume: "240 kg", channel: "Hotelkette · São Tomé" },
          { window: "KW 28", crop: "Freilandeier (Aviarium A1)", volume: "3.400 Stk.", channel: "Kontinuierliche Distribution" },
        ],
        clients: [
          { group: "Lokale Landwirte", consume: "Technische Ausbildung + Produktion" },
          { group: "Schulen & Universitäten", consume: "Feldtage + IoT-Curriculum" },
          { group: "Kommunen & Regierung", consume: "Replizierbares Modell + Daten" },
          { group: "NGOs & Kooperation", consume: "Technologie + Regeneration" },
        ],
      },
      cv: {
        country_label: "Kap Verde · 10 Inseln · Phase 1: 5 aktiv",
        title_a: "HX Kap Verde", title_b: "Insulares Energie-Hub",
        status_badge: "Insulares Netz · Stabilisiert",
        featured_status: "Live-Netz · Insular Stabilisiert",
        featured_tag: "Solar · BESS · Smart Grid",
        lede: "Archipelagische Energieunabhängigkeit — konzipiert und betrieben von HannoNexus (HN AI): utility-scale Solarproduktion, BESS-LFP-Speicherung, intelligente Verteilung zwischen den Inseln und lokale technische Ausbildung.",
        featured_lede: "Archipelagische Energieunabhängigkeit: utility-scale Solarproduktion, BESS-LFP-Speicherung, intelligente Verteilung zwischen den Inseln, orchestriert durch HN AI, und HX Academy zur Ausbildung lokaler Talente.",
        kpi_solar: "Installiert Solar", kpi_solar_sub: "Santiago · Sal · Boa Vista",
        kpi_bess: "BESS-Speicherung", kpi_bess_sub: "LFP · 261 kWh / Modul",
        kpi_wind: "Wind aktiv", kpi_wind_sub: "Sal · Vestas V90",
        kpi_diesel: "Diesel ersetzt", kpi_diesel_sub: "vs. Baseline 2024",
        loop_eyebrow: "Physische Schicht · Energiekreislauf",
        loop_title: "Utility-scale Solar → BESS → Smart Grid zwischen den Inseln",
        loop_badge: "Kreislauf · Solar → BESS → Smart Grid",
        loop_overlay_title: "Integrierter insularer Energiefluss",
        loop_overlay_sub: "Huawei FusionSolar · CATL LFP · Vestas · HN-AI-Orchestrierung.",
        tel_solar_brand: "Huawei FusionSolar · Utility-Scale", tel_solar_title: "Photovoltaische Produktion",
        tel_solar_r1: "Momentane Erzeugung", tel_solar_r2: "Eigenverbrauch heute", tel_solar_r3: "Exportierter Überschuss",
        tel_bess_brand: "BESS LFP · CATL 261 kWh", tel_bess_title: "Verteilte Speicherung",
        tel_bess_r1: "Durchschnittlicher SoC der Flotte", tel_bess_r2: "Zyklen heute", tel_bess_r3: "Sicherheitsreserve", tel_bess_r3_val: "4,0 h · 50 Hz Frequenz",
        tel_grid_brand: "Smart Grid zwischen den Inseln", tel_grid_title: "HN-AI-Orchestrierung",
        tel_grid_r1: "Verbundene Inseln", tel_grid_r1_val: "5 / 10 · Phase 1", tel_grid_r2: "Dispatch-Latenz", tel_grid_r3: "Monatliche Netz-Uptime",
        matrix_eyebrow: "Verarbeitungsschicht · Energie-Matrix",
        matrix_title: "Vier operative Säulen, überwacht von HN AI",
        sec_solar: "Solarerzeugung", sec_solar_status: "Utility-Scale · in Hochlauf", sec_solar_kpi: "Kapazitätsfaktor",
        sec_bess: "BESS-Speicherung", sec_bess_status: "LFP-Flotte betriebsbereit", sec_bess_kpi: "Verfügbarkeit",
        sec_ev: "EV-Netz · Praia", sec_ev_status: "Fast-Charge aktiv", sec_ev_kpi: "Durchschnittliche Auslastung",
        sec_grid: "Smart Grid Inseln", sec_grid_status: "HN-AI-Orchestrierung 24/7", sec_grid_kpi: "Stabile Frequenz",
        forecast_eyebrow: "Interface-Schicht · Entscheidung in Echtzeit",
        forecast_title: "HN-AI-Meldungen und Energieprognose zwischen den Inseln",
        forecast_head: "Energieprognose",
        col_window: "Zeitfenster", col_event: "Ereignis", col_forecast: "Prognose", col_action: "AI-Aktion", col_confidence: "Konfidenz",
        impact_eyebrow: "Sozialer Impact · Multi-Client-Modell",
        impact_title: "HX Academy Kap Verde & Beneficiary-Ökosystem",
        academy_eyebrow: "Technische Ausbildung", academy_title: "HX Academy · Praia",
        academy_lede: "Zertifizierte Curricula in Photovoltaik-Installation, BESS-Betrieb, Inbetriebnahme von Ladestationen und EV-Diagnose. HannoNexus überführt die besten Absolventen in das HX Install Network und den HX Talent Pool — und reduziert damit die Abhängigkeit von expatriierten Arbeitskräften.",
        academy_photo_tag: "Kohorte · Solar & BESS Zertifizierung",
        academy_photo_caption: "Junge Kapverdier in HannoNexus-zertifizierter technischer Ausbildung.",
        mini_students: "Zertifizierte Studierende 2026", mini_classes: "Aktive Kohorten", mini_jobs: "Direkte Arbeitsplätze geschaffen",
        cta_title: "Die insulare Blaupause, bereit zur Skalierung über die gesamte CPLP.",
        cta_lede: "Die HX-Kap-Verde-Architektur ist modular: Solar + BESS + Smart Grid + Academy — replizierbar in São Tomé, Angola, Mosambik, Guinea-Bissau und Timor-Leste, integriert in dieselbe HannoNexus AI.",
        preview_footer: "Phase 1 · 5 verbundene Inseln · 183 zertifizierte Studierende",
        alerts: [
          { time: "vor 3 Min.", title: "Solar-Spitzenproduktion in Santiago erkannt", action: "BESS startete beschleunigte Ladung (94 kW): Reserve für die Abendspitze auf Boa Vista." },
          { time: "vor 9 Min.", title: "Insel-Rebalancing · Santiago → Fogo", action: "HN AI leitete 128 kWh über Seekabel um, um die Frequenz bei 50 Hz zu stabilisieren." },
          { time: "vor 31 Min.", title: "Anhaltender Wind > 9 m/s auf Sal", action: "Vestas-Turbinen mit voller Kapazität (2,4 MW); Reduktion des Dieselverbrauchs im Thermokraftwerk bestätigt." },
        ],
        forecast: [
          { window: "Heute 18:00", event: "Verbrauchsspitze · Praia", forecast: "24,6 MW", action: "BESS vorgeladen" },
          { window: "Morgen 12:00", event: "Solar-Überschuss · Santiago", forecast: "+18 %", action: "Export via Smart Grid" },
          { window: "KW 25", event: "Präventivwartung · Sal WT-04", forecast: "6 h", action: "Zeitfenster um 03:00 (schwacher Wind)" },
          { window: "KW 26", event: "BESS-Verstärkung · Boavista", forecast: "+2 MWh", action: "Container 07 trifft am 12. ein" },
        ],
        clients: [
          { group: "ELECTRA · Nationaler Versorger", consume: "Energie + Smart Grid" },
          { group: "Insel-Hotellerie", consume: "Solar + BESS + EV-Flotte" },
          { group: "Ländliche Insel-Kommunen", consume: "Microgrids + Ausbildung" },
          { group: "Berufsschulen & HX Academy", consume: "Curriculum + Talent Pool" },
        ],
      },
    },
  },

  fr: {
    brand: { powered_by: "Powered by BSGOTECH", tagline: "Integrated Infrastructure Ecosystem" },
    nav: { home: "Accueil", hx_energy: "HX Energy", hx_automotive: "HX Automotive", hx_systems: "HX Systems", hx_infrastructure: "HX Infrastructure", hx_academy: "HX Academy", projects: "Projets", contact: "Contact", cta_partnerships: "Partenariats Stratégiques" },
    search: {
      trigger: "Rechercher", placeholder: "Rechercher pages, projets, écosystèmes…",
      empty: "Aucun résultat", hint_navigate: "Naviguer", hint_select: "Ouvrir", hint_close: "Fermer",
      groups: { pages: "Pages", ecosystems: "Écosystèmes HX", projects: "Projets", presentations: "Présentations" },
    },
    mou: {
      badge: "MEMORANDUM OF UNDERSTANDING",
      title: "Memorandum of Understanding",
      subtitle: "Construire un partenariat stratégique pour une énergie durable au Cap-Vert et dans la CPLP.",
      cta_download: "Télécharger le MOU", cta_request: "Demander un partenariat", cta_login: "Espace Partenaires",
      logos_row: "Écosystème de partenaires stratégiques",
      overview: {
        eyebrow: "Vue d'ensemble du partenariat", title: "Une alliance industrielle", accent: "à long terme.",
        cards: [
          { icon: "target",   title: "Mission",            body: "Démocratiser l'accès au solaire utility, au stockage et à la mobilité électrique dans les pays lusophones grâce à la technologie OEM tier-1 et à l'industrialisation locale." },
          { icon: "eye",      title: "Vision",             body: "Faire du Cap-Vert et de São Tomé-et-Príncipe des hubs de référence CPLP dans les énergies renouvelables — avec autonomie technique, financière et éducative d'ici 2030." },
          { icon: "flag",     title: "Objectifs",          body: "Fournir, installer et exploiter des systèmes PV, BESS et smart-grid — avec transfert technologique, emploi qualifié et formation certifiée." },
          { icon: "compass",  title: "Stratégie à long terme", body: "Modèle réplicable dans les 9 pays de la CPLP : OEM + private-label + assemblage local + academy — orchestré par la plateforme HannoNexus." },
        ],
      },
      structure: {
        eyebrow: "Structure du partenariat", title: "L'organisation du", accent: "réseau HannoNexus.",
        nodes: {
          root:      { title: "HX Ecosystems | BSGOTECH",       sub: "Opérateur · Architecture & Gouvernance" },
          hn:        { title: "HannoNexus Solar",                sub: "Plateforme commerciale CPLP" },
          network:   { title: "Réseau de partenariats stratégiques", sub: "Fabricants · Services · Institutions" },
          bluesun:   { title: "Bluesun Solar",                   sub: "Partenaire stratégique de fabrication énergétique", tag: "Manufacturing" },
          services:  { title: "Partenaires commerciaux de services", sub: "Services numériques et de gestion" },
          dwh:       { title: "Digital Wise Hustle",             sub: "Partenaire Services Numériques", tag: "Digital",
                       services: ["Marketing Digital", "Branding", "Développement Web", "Croissance", "Transformation Numérique"],
                       model: "HannoNexus commercialise ces services et perçoit une marge commerciale." },
          aaban:     { title: "AABAN Solutions",                 sub: "Partenaire Solutions Entreprises", tag: "Business",
                       services: ["Conseil aux entreprises", "Solutions corporate", "Gestion de projet", "Conseil stratégique"],
                       model: "HannoNexus commercialise ces services et perçoit une marge commerciale." },
        },
      },
      bluesun: {
        eyebrow: "Bluesun Solar", title: "Partenaire stratégique de", accent: "fabrication énergétique.",
        lede: "Bluesun Solar est le partenaire OEM de fabrication d'HannoNexus — modules photovoltaïques, onduleurs et systèmes de batteries avec ingénierie certifiée, documentation technique et certifications internationales.",
        pills: ["Fabrication solaire", "OEM", "Private Label", "Modules PV", "Onduleurs", "Systèmes de batterie", "Support Technique", "Ingénierie", "Documentation", "Certifications", "Expansion conjointe"],
        flow: { manufacturer: "Fabricant", hannonexus: "HannoNexus Solar", customer: "Client final",
                m_desc: "Bluesun · OEM tier-1", h_desc: "Commercialisation · Ingénierie · Service", c_desc: "Gouvernements · Entreprises · Utilities" },
      },
      areas: {
        eyebrow: "Domaines de coopération", title: "Où le partenariat", accent: "crée de la valeur.",
        list: [
          { icon: "package",     title: "Fourniture d'équipements",  desc: "Panneaux, onduleurs, BESS et câbles d'origine OEM certifiée." },
          { icon: "factory",     title: "Fabrication OEM",           desc: "Spécification et fabrication de produits co-développés." },
          { icon: "tag",         title: "Private Label",             desc: "Portfolio HannoNexus avec identité et garantie propres." },
          { icon: "cpu",         title: "Ingénierie",                desc: "Conception, dimensionnement, simulation et as-built." },
          { icon: "sun",         title: "Projets solaires",          desc: "Projets clés en main utility, commerciaux et résidentiels." },
          { icon: "graduation",  title: "Formation technique",       desc: "HX Academy · cursus certifiés d'installation et O&M." },
          { icon: "wrench",      title: "Service Après-Vente",       desc: "Réseau de service autorisé et pièces d'origine." },
          { icon: "network",     title: "Réseau d'installation",     desc: "Partenaires locaux certifiés HX Install Network." },
          { icon: "globe",       title: "Services numériques",       desc: "Digital Wise Hustle · marketing, branding, web, growth." },
          { icon: "briefcase",   title: "Solutions entreprises",     desc: "AABAN Solutions · conseil, PMO, advisory stratégique." },
          { icon: "map",         title: "Expansion régionale",       desc: "Réplication en Angola, Mozambique, GB, Timor." },
        ],
      },
      benefits: {
        eyebrow: "Pourquoi devenir partenaire", title: "Quatre piliers de", accent: "valeur mutuelle.",
        cards: [
          { icon: "trending-up", title: "Pour HannoNexus",    body: "Accès à la fabrication OEM tier-1, portfolio private-label et vitesse de go-to-market dans la CPLP." },
          { icon: "layers",      title: "Pour Bluesun Solar", body: "Canal commercial structuré dans les marchés lusophones, assemblage local et représentation technique." },
          { icon: "users",       title: "Pour les clients",   body: "Prix compétitifs, garantie locale, service technique de proximité et formation certifiée." },
          { icon: "flag",        title: "Pour le Cap-Vert",   body: "Emploi qualifié, réduction de la dépendance énergétique et transfert technologique réel." },
        ],
      },
      roadmap: {
        eyebrow: "Feuille de route", title: "Six phases.", accent: "Un plan.",
        phases: [
          { phase: "Phase 1", title: "Signature du MOU",           desc: "Formalisation de l'engagement stratégique entre les parties." },
          { phase: "Phase 2", title: "Enregistrement de la société", desc: "HX Ecosystems | BSGOTECH Lda. · siège au Cap-Vert." },
          { phase: "Phase 3", title: "Premier conteneur solaire",  desc: "Arrivée et dédouanement du premier lot d'équipements OEM." },
          { phase: "Phase 4", title: "Solar Experience Center",    desc: "Showroom technique et démo permanente à Praia." },
          { phase: "Phase 5", title: "Installer Academy",          desc: "Première promotion HX Academy · certification nationale." },
          { phase: "Phase 6", title: "Expansion régionale",        desc: "Réplication à STP, en Angola, au Mozambique et en Guinée-Bissau." },
        ],
      },
      team: {
        eyebrow: "Équipe", title: "Une équipe technique", desc_placeholder: "", accent: "basée localement.",
        roles: [
          { role: "CEO & Fondateur",         icon: "star" },
          { role: "Équipe d'installation",   icon: "hard-hat" },
          { role: "Ingénierie",              icon: "cpu" },
          { role: "Opérations terrain",      icon: "map" },
          { role: "Finance",                 icon: "line-chart" },
          { role: "Marketing & Stratégie",   icon: "megaphone" },
          { role: "Partenaires techniques",  icon: "handshake" },
        ],
      },
      gallery: { eyebrow: "Galerie de projets", title: "L'œuvre livrée.", accent: "Image par image." },
      docs: {
        eyebrow: "Centre documentaire", title: "Documents publics", accent: "et privés.",
        public_title: "Documents publics",
        public: [
          { icon: "file-text", label: "MOU PDF" },
          { icon: "book",      label: "Catalogue produit" },
          { icon: "slides",    label: "Présentation institutionnelle" },
          { icon: "award",     label: "Certificats" },
        ],
        private_title: "Portail Partenaires Privé",
        private: [
          { icon: "file-signature", label: "Contrats" },
          { icon: "receipt",        label: "Facture pro forma" },
          { icon: "file-code",      label: "Documents techniques" },
          { icon: "briefcase",      label: "Accords commerciaux" },
          { icon: "line-chart",     label: "Rapports" },
        ],
        login_hint: "Accès protégé par mot de passe", login_cta: "Entrer au portail",
      },
      form: {
        eyebrow: "Demander un partenariat", title: "Construisons", accent: "ensemble.",
        lede: "Remplissez le formulaire et l'équipe HannoNexus répondra dans les 48h ouvrées.",
        fields: {
          company: "Société", country: "Pays", website: "Site web",
          contact: "Personne de contact", email: "E-mail", phone: "Téléphone",
          type: "Type de partenariat",
          type_options: ["Distributeur", "Installateur", "Investisseur", "Partenaire Technologique", "Gouvernement", "Partenaire Business"],
          message: "Message",
        },
        submit: "DEMANDER UN PARTENARIAT", success: "Demande reçue — réponse sous 48h.", error: "Envoi impossible. Veuillez réessayer.",
      },
    },
    hero: {
      subtitle_p1: "Integrated", subtitle_accent: "Infrastructure Ecosystem",
      keywords: ["Énergie", "Mobilité", "Infrastructure", "Systèmes Numériques"],
      lede: "HannoNexus conçoit et opère des écosystèmes intégrés d'énergie renouvelable, de mobilité électrique, d'infrastructure intelligente et de plateformes numériques — accélérant le développement durable des pays lusophones.",
      cta_explore: "Explorer l'écosystème", cta_partnerships: "Partenariats Stratégiques", scroll_hint: "Faites défiler",
    },
    vision: {
      eyebrow: "La Vision", title: "Une Plateforme.", accent: "Un Écosystème.",
      p: "HX Ecosystems est le cœur opérationnel de HannoNexus — cinq couches intégrées couvrant toute la chaîne de valeur, de la production d'énergie à la mobilité, en passant par l'infrastructure physique, les plateformes numériques et la formation technique.",
    },
    ecosystems: {
      eyebrow: "HX Ecosystems", title: "Le cœur opérationnel de", accent: "HannoNexus",
      lede: "Cinq écosystèmes intégrés. Une seule plateforme. Un seul opérateur.",
      includes: "Inclut", learn_more: "En savoir plus",
      strategic_badge: "Projet Stratégique",
    },
    eco: {
      "hx-energy": {
        name: "HX HannoNexus Energy", short: "HX Energy", tagline: "Écosystème énergétique intelligent",
        description: "Énergie intelligente pour la production, le stockage et la distribution. Du soleil au réseau, du panneau au point de charge — une chaîne de valeur totalement intégrée.",
        subs: {
          "HX Solar": "Production photovoltaïque résidentielle, commerciale et utility-scale.",
          "HX Core": "Systèmes de stockage (BESS) résidentiels, commerciaux et industriels.",
          "HX Pulse": "Plateforme de monitoring temps réel, IA et télémétrie.",
          "HX Charge": "Réseau de recharge AC/DC pour VE — cafés, hôtels, corridors autoroutiers.",
          "HX Install Network": "Réseau certifié de partenaires d'installation et de maintenance.",
          "HX Academy": "Formation et certification techniques en solaire et stockage.",
        },
      },
      "hx-automotive": {
        name: "HX HannoNexus Automotive", short: "HX Automotive", tagline: "Écosystème de mobilité électrique",
        description: "Mobilité électrique complète — du citadin compact au grand tourer, des vélos OBOWAY aux motos électriques. Marque private-label conçue pour les marchés lusophones, avec assemblage local et service autorisé.",
        subs: {
          "HX EV": "Portefeuille private-label de véhicules électriques : Nudi, Zuri, Pirys, Mionga, Mionga Cargo, Atlan.",
          "OBOWAY": "Marque propre de mobilité légère : e-bikes et motos 100 % électriques.",
          "HX Assembly": "Centres d'assemblage locaux avec transfert technologique OEM.",
          "HX Battery": "Batteries de traction, second-life et systèmes de stockage véhiculaires.",
          "HX Mobility": "Plateforme de mobilité partagée et services urbains.",
          "HX Fleet": "Gestion intelligente de flotte avec télémétrie et optimisation de routes.",
          "HX Service": "Réseau de service autorisé et pièces OEM.",
        },
        vehicles: {
          "HX Nudi": { segment: "Compact Urban EV", desc: "Citadine électrique compacte — mobilité urbaine quotidienne, idéale pour villes et îles." },
          "HX Zuri": { segment: "Executive Sedan", desc: "Berline exécutive électrique à autonomie étendue — mobilité corporate & premium." },
          "HX Pirys": { segment: "Adventure SUV", desc: "SUV électrique off-road pour terrains volcaniques et routes côtières." },
          "HX Mionga": { segment: "Utility Pickup", desc: "Pickup électrique utilitaire — agriculture, travaux publics et flottes de service." },
          "HX Mionga Cargo": { segment: "Commercial Van", desc: "Fourgon électrique de cargo pour logistique last-mile et distribution commerciale." },
          "HX Atlan": { segment: "Flagship Grand Tourer", desc: "Grand tourer électrique haut de gamme — la signature HannoNexus." },
          "OBOWAY E-Bike": { segment: "Electric Bicycle · OBOWAY", desc: "E-bike OBOWAY — mobilité légère urbaine et touristique, idéale pour îles et centres historiques." },
          "OBOWAY Moto": { segment: "Electric Motorcycle · OBOWAY", desc: "Moto électrique OBOWAY — solution agile de mobilité urbaine et livraison last-mile zéro émission." },
        },
      },
      "hx-systems": {
        name: "HX Systems", short: "HX Systems", tagline: "Plateformes numériques & infrastructure technologique",
        description: "La couche numérique qui alimente toute l'opération HannoNexus — cloud souverain, intelligence artificielle, données et IoT industriel.",
        subs: {
          "HX Cloud": "Cloud public, hybride et edge — infrastructure scalable et souveraine.",
          "HX AI": "Modèles IA, LLMs privés, vision par ordinateur et optimisation prédictive.",
          "HX HUB": "Hub numérique unifié — dashboards, workflows et intégrations entreprise.",
          "HX Data": "Data lakes, warehouses et pipelines d'analytics temps réel.",
          "HX IoT": "Plateforme IoT industrielle — capteurs, télémétrie et gestion de dispositifs.",
        },
      },
      "mionga": {
        name: "MIONGA Beverages", short: "MIONGA", tagline: "Écosystème premium de lifestyle",
        description: "Boissons signature, café artisanal Moin | Olá, formats flagship et alliances stratégiques entre l'Europe (Uetze) et la CPLP — la marque art de vivre du groupe HannoNexus.",
        subs: {
          "MIONGA Water":     "Eau minérale naturelle · pH 7,2 · Riche en potassium.",
          "MIONGA Sparkling": "Eau minérale gazeuse · Rafraîchissante et équilibrée.",
          "MIONGA Juice":     "Jus 100% naturels · Sans conservateurs.",
          "MIONGA Iced Tea":  "Thé glacé premium · Ingrédients sélectionnés.",
          "MIONGA Coffee":    "Blend premium · 100% Arabica.",
          "Moin | Olá Cafés": "Format flagship urbain avec Kinderecke, bar et retail.",
        },
      },
      "hx-infrastructure": {
        name: "HX Infrastructure", short: "HX Infrastructure", tagline: "Infrastructures intelligentes",
        description: "Nous construisons l'infrastructure physique des villes de demain — réseaux électriques, éclairage public, eau et stockage distribué.",
        subs: {
          "Smart Cities": "Gestion urbaine intégrée — mobilité, énergie, sécurité.",
          "Microgrids": "Réseaux électriques autonomes pour communautés et zones industrielles.",
          "Water Systems": "Gestion intelligente de l'eau — dessalement, traitement, distribution.",
          "Public Lighting": "Éclairage public LED avec IoT — télémétrie, dimming, capteurs.",
          "Energy Storage": "Stockage distribué pour stabilité et résilience du réseau.",
        },
      },
      "hx-academy": {
        name: "HX Academy", short: "HX Academy", tagline: "Formation & certification",
        description: "Nous formons et certifions les professionnels qui opèrent l'écosystème HannoNexus — ingénierie, énergie, mobilité, logiciel et IA.",
        subs: {
          "Energy": "Installation et opération de systèmes solaires, BESS et réseaux intelligents.",
          "Mobility": "Maintenance VE, gestion de flotte et infrastructure de recharge.",
          "Software": "Ingénierie logicielle, cloud et développement de plateformes.",
          "AI": "Machine learning appliqué, vision par ordinateur et IA opérationnelle.",
          "Engineering": "Ingénierie électrique, mécanique et systèmes industriels.",
        },
      },
    },
    how: {
      eyebrow: "Comment ça marche", title: "De l'énergie à la", accent: "décision en temps réel.", phase: "Phase",
      steps: [
        { step: "Production d'énergie",       desc: "Solaire · Éolien · Cogénération" },
        { step: "Stockage intelligent",       desc: "BESS résidentiel · commercial · industriel" },
        { step: "Gestion numérique",          desc: "HX Cloud · HX Data · HX HUB" },
        { step: "Infrastructure",             desc: "Smart Grid · Microgrids · Éclairage public" },
        { step: "Mobilité électrique",        desc: "VE · Flotte · Charging" },
        { step: "Monitoring IA",              desc: "HX Pulse · HX AI · Maintenance prédictive" },
        { step: "Analytique temps réel",      desc: "Dashboards exécutifs · KPIs · Alertes" },
      ],
    },
    projects: {
      eyebrow: "Projets Stratégiques", title: "Le déploiement a commencé dans les", accent: "pays lusophones.",
      components: "Composants de l'Écosystème", location: "Localisation", start: "Début", jobs: "Emplois",
      next_markets: "Prochains Marchés",
      next_markets_desc: "La réplication du modèle intégré HannoNexus est prévue pour les autres pays de la CPLP à partir de 2030.",
      cta_title_p1: "Gouvernement, investisseur institutionnel ou", cta_title_accent: "partenaire stratégique ?",
      cta_lede: "Nous opérons en modèles BOT, joint-venture, private-label OEM et concessions publiques.",
      cta_button: "Démarrer un partenariat",
      list: {
        "cabo-verde": {
          country: "Cap-Vert", focus: "HX Energy", tagline: "Indépendance énergétique de l'archipel.",
          description: "Déploiement d'un écosystème énergétique complet — production solaire, stockage, distribution intelligente et formation technique locale.",
          highlights: ["Énergie Solaire", "BESS (Stockage Batterie)", "Smart Grid inter-îles", "Centres Logistiques", "HX Academy"],
        },
        "sao-tome": {
          country: "Sao Tomé-et-Principe", focus: "HX Automotive", tagline: "Mobilité électrique insulaire.",
          description: "Écosystème de mobilité électrique avec assemblage local, réseau de recharge, gestion numérique de flotte et formation technique.",
          highlights: ["Véhicules Électriques", "Centre d'Assemblage", "Réseau de Recharge", "Plateforme Numérique", "Formation Technique"],
        },
        "mionga": {
          country: "Uetze → Cap-Vert", focus: "MIONGA Beverages", tagline: "La pureté qui voyage. La qualité qui reste.",
          description: "Écosystème premium de lifestyle et d'hospitalité — boissons signature, café artisanal, formats flagship et alliances stratégiques entre l'Europe et la CPLP.",
          highlights: ["MIONGA Water · Sparkling · Juice · Iced Tea · Coffee", "Moin | Olá Coffee Connection · Uetze HQ", "Route Atlantique ~3 900 km", "Flotte Cargo Électrique · Zéro Émissions", "Hôtellerie premium & Réseau F&B"],
          linkLabel: "Explorer MIONGA",
        },
      },
    },
    pulse: {
      eyebrow: "Plateforme Intelligente", title: "HX Pulse",
      lede: "Le hub numérique qui surveille toute l'opération HannoNexus en temps réel — de la cellule de batterie au chargeur, du panneau solaire au dashboard exécutif.",
      features: ["Intelligence Artificielle", "Dashboards Exécutifs", "Télémétrie Industrielle", "Maintenance Prédictive", "Monitoring 24/7", "Connectivité Starlink"],
      chart_label: "Flux d'énergie · 24h", chart_status: "Temps réel",
      metrics: [
        { k: "Solar Yield", v: "12,4 MW", sub: "+8,2%" },
        { k: "ESS SoC", v: "84%", sub: "Nominal" },
        { k: "Sessions VE", v: "1 247", sub: "Aujourd'hui" },
        { k: "CO₂ évité", v: "42,1 t", sub: "Ce mois" },
      ],
    },
    impact: {
      eyebrow: "Impact", title: "Des chiffres qui", accent: "mesurent l'avenir.",
      stats: [
        { value: "20 000+", label: "Systèmes installés" },
        { value: "60 MW",   label: "Énergie propre" },
        { value: "500+",    label: "Professionnels certifiés" },
        { value: "300+",    label: "Emplois qualifiés" },
        { value: "15 000 t", label: "CO₂ évité" },
      ],
    },
    roadmap: {
      eyebrow: "Roadmap", title: "Cinq ans.", accent: "Cinq jalons.",
      items: [
        { year: "2026", milestone: "Planification Stratégique",         desc: "Modélisation financière, partenariats, permis, ingénierie préliminaire." },
        { year: "2027", milestone: "Hub Logistique & HX Academy",       desc: "Ouverture du premier hub logistique et campus de formation." },
        { year: "2028", milestone: "Opération HX Energy",               desc: "Mise en service solaire, BESS et smart grid au Cap-Vert." },
        { year: "2029", milestone: "Opération HX Automotive",           desc: "Début de l'assemblage local VE et du réseau de recharge à STP." },
        { year: "2030", milestone: "Expansion Régionale",               desc: "Angola, Mozambique, Guinée-Bissau — réplication du modèle intégré." },
      ],
    },
    partners: {
      eyebrow: "Partenaires Stratégiques", title: "Construit avec", accent: "des institutions qui construisent des nations.",
      categories: ["Gouvernements", "Fabricants OEM", "Institutions Financières", "Fonds ESG", "Partenaires Technologiques"],
    },
    investment: {
      eyebrow: "Modèle d'Investissement", title: "OEM +", accent: "Private Label.",
      lede: "HannoNexus intègre la technologie OEM Tier-1 internationale à l'assemblage local, aux opérations locales et au service local. Un modèle éprouvé qui transfère une valeur tangible au pays d'accueil, sans compromettre la qualité industrielle.",
      advantages: ["Industrialisation locale", "Transfert technologique", "Réduction des coûts opérationnels", "Création d'emplois qualifiés", "Scalabilité régionale"],
    },
    about: {
      eyebrow: "À propos de HannoNexus", title: "Une plateforme dédiée aux", accent: "pays lusophones.",
      p: "HannoNexus est une plateforme dédiée au développement d'écosystèmes d'énergie, de mobilité et d'infrastructure intelligente, offrant des solutions technologiques adaptées aux besoins des pays lusophones — Cap-Vert, Sao Tomé-et-Principe, Angola, Mozambique, Guinée-Bissau, Timor-Leste.",
    },
    contact_banner: {
      title: "Construisons", accent: "l'avenir ensemble.",
      lede: "Gouvernements, investisseurs institutionnels, fabricants OEM et partenaires technologiques — nous vous répondons sous 48 heures.",
      cta: "Partenariats Stratégiques",
    },
    people: {
      eyebrow: "Nos Équipes", title: "Fait par des gens.", accent: "Opéré dans les pays lusophones.",
      lede: "HannoNexus est opéré par des ingénieurs, techniciens et formateurs locaux — du Cap-Vert, de Sao Tomé-et-Principe et de la diaspora lusophone. Formation certifiée, emploi qualifié, fierté du travail accompli.",
      roles: {
        role_solar: "Technicienne Solaire · HX Install Network",
        role_control: "Ingénieure d'Opérations · HX Pulse",
        role_mechanic: "Technicien VE · HX Service",
        role_academy: "Stagiaires · HX Academy",
        role_network: "Équipe HX Install Network",
        role_engineer: "Ingénieure Senior · HX Energy",
      },
    },
    eco_page: {
      portfolio_eyebrow: "Portfolio", portfolio_title: "Ce qui compose",
      lineup_eyebrow: "Private Label · Flotte de véhicules", lineup_title: "La flotte", lineup_accent: "HannoNexus.",
      lineup_lede: "Portfolio private-label complet : six voitures électriques HX plus deux OBOWAY de mobilité légère — du citadin compact au grand tourer flagship, du e-bike urbain à la moto électrique.",
      private_label_electric: "Private-label · 100% électrique", model: "Modèle",
      values: [
        { k: "Intégré", v: "Une seule plateforme opérationnelle couvre toute la chaîne de valeur." },
        { k: "Scalable", v: "Modèle réplicable — d'un pilote local à une expansion continentale." },
        { k: "Certifié", v: "Matériel TÜV · CE · IEC de partenaires OEM tier-1." },
      ],
      others_eyebrow: "Autres Écosystèmes", others_title: "Explorez le reste de la plateforme HannoNexus.",
      cta_title: "Intéressé par le déploiement de", cta_lede: "Nous préparons un plan de déploiement sur mesure en 48 heures.",
      cta_button: "Parler à l'équipe",
    },
    footer: {
      tagline_l1: "Énergie · Mobilité · Infrastructure ·", tagline_l2: "Systèmes Numériques.",
      contact: "Contact", operator: "Opérateur", operated_by: "Intégré & opéré par",
      operator_name: "BSGOTECH Infrastructure Systems",
      ecosystems: "HX Ecosystems", markets: "Marchés Stratégiques",
      copyright: "HannoNexus · Integrated Infrastructure Ecosystem",
      architected: "Architected in Germany", cplp_badge: "CPLP · Powered by BSGOTECH",
    },
    dashboards: {
      common: {
        online: "Système en Ligne & Automatisé", live_dashboard: "Tableau de Bord en Direct",
        open_dashboard: "Ouvrir le Tableau de Bord", request_demo: "Demander une démonstration",
        request_meeting: "Solliciter une Réunion Exécutive", other_projects: "Voir les autres projets",
        recent_alerts: "Alertes Récentes", live_feed: "HN AI · Flux en Direct",
        ai_recommended: "Recommandé par HN AI", client_ecosystem: "Écosystème Client",
        who_consumes: "Qui consomme quoi", replicability: "Réplicabilité",
        preview_telemetry: "Aperçu · Télémétrie en Direct", grid_telemetry: "Aperçu · Télémétrie du Réseau",
      },
      biopinto: {
        country_label: "São Tomé-et-Príncipe · Projet Pilote",
        title_a: "BioPintoSTP", title_b: "Ferme Modèle Connectée",
        lede: "Surveillance en temps réel via la Plateforme HannoNexus (HN AI), capteurs IoT intégrés et imagerie satellite CropWatch — un jumeau numérique opérationnel pour l'agriculture de précision à São Tomé-et-Príncipe.",
        featured_lede: "Le premier jumeau numérique agricole intégré de HannoNexus : surveillance IoT en temps réel, analyse satellite CropWatch, décisions pilotées par HN AI et un modèle réplicable dans tous les pays de la CPLP.",
        featured_tag: "IoT · Satellite · HN AI",
        kpi_solar: "Production Solaire", kpi_solar_sub: "Huawei FusionSolar · temps réel",
        kpi_water: "Économie d'Eau", kpi_water_sub: "vs. arrosage manuel (mois en cours)",
        kpi_nodes: "Capteurs Actifs", kpi_nodes_sub: "Nœuds IoT en ligne / 130 au total",
        kpi_sat: "Passages Satellite", kpi_sat_sub: "CropWatch · NDVI + Thermique",
        loop_eyebrow: "Couche Physique · Boucle Fermée",
        loop_title: "Énergie solaire → Pompage → Irrigation → Retour",
        loop_badge: "Boucle Physique · Solaire → Pompe → Irrigation",
        loop_overlay_title: "Flux intégré énergie ↔ eau",
        loop_overlay_sub: "Huawei FusionSolar · DIFFUL · Haishun · Zéro diesel.",
        tel_solar_brand: "Huawei FusionSolar", tel_solar_title: "Production Photovoltaïque",
        tel_solar_row1: "Production actuelle", tel_solar_row2: "Consommation diesel", tel_solar_row2_val: "0 L · Zéro Diesel", tel_solar_row3: "Autoconsommation aujourd'hui",
        tel_water_brand: "Pompage DIFFUL & Irrigation Haishun", tel_water_title: "Cycle Hydrique",
        tel_water_row1: "Débit d'eau", tel_water_row1_val: "Optimisé (boucle IA)", tel_water_row2: "Économie cumulée", tel_water_row3: "Volume du réservoir",
        matrix_eyebrow: "Couche de Traitement · Matrice IoT",
        matrix_title: "Quatre secteurs productifs supervisés par HN AI",
        sec_avi: "Aviculture", sec_avi_status: "Cash-flow constant", sec_avi_kpi: "Sécurité Protéique",
        sec_viv: "Pépinière", sec_viv_status: "Régénération locale active", sec_viv_kpi: "Capacité Utilisée",
        sec_flo: "Fleurs Ornementales", sec_flo_status: "Focus : export à forte marge", sec_flo_kpi: "Lots Prêts",
        sec_hor: "Maraîchage & Fruits", sec_hor_status: "Irrigation intelligente active", sec_hor_kpi: "Humidité du Sol",
        forecast_eyebrow: "Couche d'Interface · Décision en Temps Réel",
        forecast_title: "Alertes HN AI et prévision de récolte",
        forecast_head: "Prévision de Récolte",
        col_window: "Fenêtre", col_crop: "Culture", col_volume: "Volume", col_channel: "Canal", col_confidence: "Confiance",
        impact_eyebrow: "Impact Social · Modèle Multi-Client",
        impact_title: "Journées de Terrain & écosystème de bénéficiaires",
        academy_eyebrow: "Formation Terrain", academy_title: "Journées de Terrain BioPintoSTP",
        academy_lede: "Sessions pratiques trimestrielles à la ferme modèle où agriculteurs locaux, étudiants des écoles agricoles et représentants municipaux sont formés à l'irrigation intelligente, la gestion photovoltaïque et la lecture des données IoT. HannoNexus certifie chaque participant et intègre les meilleurs au HX Academy Talent Pool.",
        academy_photo_tag: "Session Trimestrielle · Secteur C",
        academy_photo_caption: "Agriculteurs et étudiants locaux en formation sur l'irrigation intelligente et la lecture des données IoT.",
        mini_participants: "Participants 2026", mini_sessions: "Sessions réalisées", mini_schools: "Écoles impliquées",
        cta_title: "Un modèle conçu pour se déployer dans tous les pays CPLP.",
        cta_lede: "L'architecture BioPintoSTP est modulaire : énergie, eau, production et formation, réplicables au Cap-Vert, en Angola, au Mozambique et en Guinée-Bissau, intégrés à la même HannoNexus AI.",
        alerts: [
          { time: "il y a 4 min", title: "Alerte Climatique — Sécheresse détectée par le satellite CropWatch", action: "Irrigation Haishun ajustée automatiquement : -18 % de consommation hydrique dans les secteurs B3/B4." },
          { time: "il y a 12 min", title: "Pic solaire au-dessus de la moyenne saisonnière (+22 %)", action: "Excédent redirigé vers le pompage DIFFUL — eau stockée dans la citerne supérieure." },
          { time: "il y a 38 min", title: "Humidité du sol — Secteur Maraîchage C1 à l'état idéal", action: "Cycle d'arrosage suspendu ; économie estimée de 340 L sur les 6 prochaines heures." },
        ],
        harvest: [
          { window: "Semaine 24", crop: "Laitue Batavia (Secteur C2)", volume: "180 kg", channel: "Marché Local · STP-Sud" },
          { window: "Semaine 26", crop: "Fleurs Ornementales — Anthuriums", volume: "12 lots", channel: "Export · Lisbonne (LIS)" },
          { window: "Semaine 27", crop: "Tomate Cerise (Secteur C4)", volume: "240 kg", channel: "Chaîne Hôtelière · São Tomé" },
          { window: "Semaine 28", crop: "Œufs Plein Air (Aviaire A1)", volume: "3 400 un.", channel: "Distribution Continue" },
        ],
        clients: [
          { group: "Agriculteurs Locaux", consume: "Formation Technique + Production" },
          { group: "Écoles & Universités", consume: "Journées de Terrain + Curriculum IoT" },
          { group: "Municipalités & Gouvernement", consume: "Modèle Réplicable + Données" },
          { group: "ONG & Coopération", consume: "Technologie + Régénération" },
        ],
      },
      cv: {
        country_label: "Cap-Vert · 10 Îles · Phase 1 : 5 Actives",
        title_a: "HX Cap-Vert", title_b: "Hub Énergétique Insulaire",
        status_badge: "Réseau Insulaire · Stabilisé",
        featured_status: "Réseau en Direct · Insulaire Stabilisé",
        featured_tag: "Solaire · BESS · Smart Grid",
        lede: "Indépendance énergétique archipélagique conçue et exploitée par HannoNexus (HN AI) — production solaire utility-scale, stockage BESS LFP, distribution intelligente inter-îles et formation technique locale.",
        featured_lede: "Indépendance énergétique archipélagique : production solaire utility-scale, stockage BESS LFP, distribution intelligente inter-îles orchestrée par HN AI et HX Academy pour former les talents locaux.",
        kpi_solar: "Solaire Installé", kpi_solar_sub: "Santiago · Sal · Boa Vista",
        kpi_bess: "Stockage BESS", kpi_bess_sub: "LFP · 261 kWh / module",
        kpi_wind: "Éolien Actif", kpi_wind_sub: "Sal · Vestas V90",
        kpi_diesel: "Diesel Substitué", kpi_diesel_sub: "vs. baseline 2024",
        loop_eyebrow: "Couche Physique · Boucle Énergétique",
        loop_title: "Solaire utility-scale → BESS → Smart Grid inter-îles",
        loop_badge: "Boucle · Solaire → BESS → Smart Grid",
        loop_overlay_title: "Flux énergétique insulaire intégré",
        loop_overlay_sub: "Huawei FusionSolar · CATL LFP · Vestas · orchestration HN AI.",
        tel_solar_brand: "Huawei FusionSolar · Utility-Scale", tel_solar_title: "Production Photovoltaïque",
        tel_solar_r1: "Production instantanée", tel_solar_r2: "Autoconsommation aujourd'hui", tel_solar_r3: "Excédent exporté",
        tel_bess_brand: "BESS LFP · CATL 261 kWh", tel_bess_title: "Stockage Distribué",
        tel_bess_r1: "SoC moyen de la flotte", tel_bess_r2: "Cycles aujourd'hui", tel_bess_r3: "Réserve de sécurité", tel_bess_r3_val: "4,0 h · Fréquence 50 Hz",
        tel_grid_brand: "Smart Grid Inter-Îles", tel_grid_title: "Orchestration HN AI",
        tel_grid_r1: "Îles connectées", tel_grid_r1_val: "5 / 10 · phase 1", tel_grid_r2: "Latence dispatch", tel_grid_r3: "Uptime réseau mensuel",
        matrix_eyebrow: "Couche de Traitement · Matrice Énergétique",
        matrix_title: "Quatre piliers opérationnels supervisés par HN AI",
        sec_solar: "Production Solaire", sec_solar_status: "Utility-scale · en montée en puissance", sec_solar_kpi: "Facteur de Capacité",
        sec_bess: "Stockage BESS", sec_bess_status: "Flotte LFP opérationnelle", sec_bess_kpi: "Disponibilité",
        sec_ev: "Réseau EV · Praia", sec_ev_status: "Recharge rapide active", sec_ev_kpi: "Utilisation Moyenne",
        sec_grid: "Smart Grid Inter-Îles", sec_grid_status: "Orchestration HN AI 24/7", sec_grid_kpi: "Fréquence Stable",
        forecast_eyebrow: "Couche d'Interface · Décision en Temps Réel",
        forecast_title: "Alertes HN AI et prévision énergétique inter-îles",
        forecast_head: "Prévision Énergétique",
        col_window: "Fenêtre", col_event: "Événement", col_forecast: "Prévision", col_action: "Action IA", col_confidence: "Confiance",
        impact_eyebrow: "Impact Social · Modèle Multi-Client",
        impact_title: "HX Academy Cap-Vert & écosystème de bénéficiaires",
        academy_eyebrow: "Formation Technique", academy_title: "HX Academy · Praia",
        academy_lede: "Curricula certifiés en installation photovoltaïque, exploitation BESS, mise en service de stations de recharge et diagnostic EV. HannoNexus intègre les meilleurs diplômés au HX Install Network et au HX Talent Pool — réduisant la dépendance envers la main-d'œuvre expatriée.",
        academy_photo_tag: "Cohorte · Certification Solaire & BESS",
        academy_photo_caption: "Jeunes cap-verdiens en formation technique certifiée HannoNexus.",
        mini_students: "Étudiants certifiés 2026", mini_classes: "Cohortes actives", mini_jobs: "Emplois directs créés",
        cta_title: "Le blueprint insulaire prêt à se déployer dans toute la CPLP.",
        cta_lede: "L'architecture HX Cap-Vert est modulaire : solaire + BESS + smart grid + academy, réplicables à São Tomé, en Angola, au Mozambique, en Guinée-Bissau et au Timor-Leste — intégrés à la même HannoNexus AI.",
        preview_footer: "Phase 1 · 5 îles connectées · 183 étudiants certifiés",
        alerts: [
          { time: "il y a 3 min", title: "Pic de production solaire détecté à Santiago", action: "Le BESS a lancé une charge accélérée (94 kW) : réserve pour le pic nocturne de Boa Vista." },
          { time: "il y a 9 min", title: "Rééquilibrage inter-îles · Santiago → Fogo", action: "HN AI a redirigé 128 kWh via câble sous-marin pour stabiliser la fréquence à 50 Hz." },
          { time: "il y a 31 min", title: "Vent soutenu > 9 m/s sur Sal", action: "Turbines Vestas à pleine capacité (2,4 MW) ; réduction confirmée de la combustion diesel à la centrale thermique." },
        ],
        forecast: [
          { window: "Aujourd'hui 18h00", event: "Pic de consommation · Praia", forecast: "24,6 MW", action: "BESS préchargé" },
          { window: "Demain 12h00", event: "Excédent solaire · Santiago", forecast: "+18 %", action: "Exporter via smart grid" },
          { window: "Semaine 25", event: "Maintenance préventive · Sal WT-04", forecast: "6 h", action: "Fenêtre à 03h00 (vent faible)" },
          { window: "Semaine 26", event: "Renforcement BESS · Boavista", forecast: "+2 MWh", action: "Conteneur 07 arrive le 12" },
        ],
        clients: [
          { group: "ELECTRA · Utility Nationale", consume: "Énergie + Smart Grid" },
          { group: "Hôtellerie Insulaire", consume: "Solaire + BESS + Flotte EV" },
          { group: "Municipalités Insulaires Rurales", consume: "Microgrids + Formation" },
          { group: "Écoles Techniques & HX Academy", consume: "Curriculum + Talent Pool" },
        ],
      },
    },
  },
};

/** Hook — returns HannoNexus content for current i18n language, falling back to PT. */
import { useTranslation } from "react-i18next";

export function useHannoContent() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "pt").split("-")[0];
  return HANNO_CONTENT[lang] || HANNO_CONTENT.pt;
}
